import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Form, Input, Modal, Select } from "antd";
import type {
  TCommunicationChangeAction,
  TCommunicationAuditEntry,
  TCommunicationDismissReason,
  TCommunicationQuestion,
  TCommunicationRequestAction,
  TCommunicationRequestStatus,
  TCommunicationReferenceOption,
  TCommunicationSuggestionStatus,
  TCommunicationTask,
  TCommunicationTaskFormValues,
} from "../../helpers/communication-types-helper";
import {
  buildCommunicationLogisticsPreview,
} from "../../helpers/communication-preview-helper";
import { formatCommunicationDateTime } from "../../helpers/communication-date-helper";
import { buildCommunicationOriginalEmailSections } from "../../helpers/communication-original-email-helper";
import {
  buildCommunicationTaskSteps,
  TCommunicationTaskStep,
  TCommunicationTaskStepIconKey,
} from "../../helpers/communication-step-helper";
import {
  getCommunicationConfidenceTone,
  normalizeCommunicationConfidence,
} from "../../helpers/communication-confidence-helper";
import CommunicationOriginalEmailField from "../../fields/communication-original-email-field";
import CommunicationConst from "../../communication-const";
import { Localize, localizeByKeyStatic } from "components/service";
import { successNotification } from "components/request-result";
import Icons from "components/icons";
import { useWindowSize } from "components/use-hooks";
import {
  areContextGroupsComplete,
  buildContextGroupLookupKey,
  buildFormValueSnapshot,
  buildMergedEquipmentOptions,
  buildNeutralStandToneStyle,
  buildStandToneStyle,
  createEmptyEquipmentChange,
  findMatchingContextGroup,
  formatCanonicalStandNumberLabel,
  formatStandGroupTitle,
  getCommunicationDrawerPresentationMode,
  getDefaultReadingPaneExpanded,
  getDrawerStartStep,
  getEditableChangeStandNumber,
  getEffectiveEquipmentGroupId,
  getEquipmentSuggestionMeta,
  getEquipmentValidationMessage,
  getExcludedContextGroupsFromRequestGroups,
  getInitialUnlockedStep,
  getPrimaryContextGroup,
  hasManagerNote,
  hasStandUpdates,
  isBlankEquipmentChange,
  isEquipmentChangeVisibleForContexts,
  mapTaskToFormValues,
  normalizeContextGroups,
  normalizeStandNumbers,
  normalizeTaskFormSnapshot,
  shouldIgnoreEquipmentChangeValidation,
  stripStepTitleIndex,
  type TEditableChangeGroup,
  type TValidateFieldsError,
  type TValidationFieldName,
} from "./communication-task-form-state";
import {
  CommunicationTaskClarificationsPanel,
  CommunicationTaskEquipmentChangesEditor,
  CommunicationTaskLogisticsTable,
  CommunicationTaskReplyDraftPanel,
  CommunicationTaskRequestGroups,
} from "./communication-task-form-renderers";

const { TextArea } = Input;

interface ICommunicationTaskFormProps {
  task: TCommunicationTask;
  canEditRaw: boolean;
  canApprove: boolean;
  isAuditView?: boolean;
  equipmentOptions: TCommunicationReferenceOption[];
  flooringOptions: TCommunicationReferenceOption[];
  organizerOptions: TCommunicationReferenceOption[];
  exhibitionOptions: TCommunicationReferenceOption[];
  auditEntry?: TCommunicationAuditEntry;
  onSave: (taskId: string, values: TCommunicationTaskFormValues) => void;
  onApprove: (taskId: string, values: TCommunicationTaskFormValues) => void;
  onDismissNotRelevant?: (taskId: string, reason: TCommunicationDismissReason) => void;
  onDirtyStart?: (taskId: string) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  action?: (options?: { ignoreUnsavedChanges?: boolean }) => void;
  showModal?: boolean;
}

const actionOptions: Array<{ value: TCommunicationChangeAction; label: string }> = [
  { value: "add", label: localizeByKeyStatic("COMMUNICATION.Action_Add") },
  { value: "remove", label: localizeByKeyStatic("COMMUNICATION.Action_Remove") },
  { value: "change", label: localizeByKeyStatic("COMMUNICATION.Action_Change") },
];

const actionLabelKeyMap: Record<TCommunicationChangeAction, string> = {
  add: "COMMUNICATION.Action_Add",
  remove: "COMMUNICATION.Action_Remove",
  change: "COMMUNICATION.Action_Change",
};

const requestActionLabelKeyMap: Record<TCommunicationRequestAction, string> = {
  ...actionLabelKeyMap,
  question: "COMMUNICATION.Action_Question",
};

const requestStatusLabelKeyMap: Record<TCommunicationRequestStatus, string> = {
  pending: "COMMUNICATION.RequestStatus_Pending",
  unidentified: "COMMUNICATION.RequestStatus_Unidentified",
  approved: "COMMUNICATION.RequestStatus_Approved",
  rejected: "COMMUNICATION.RequestStatus_Rejected",
  cancelled: "COMMUNICATION.RequestStatus_Cancelled",
};

const workStateLabelKeyMap: Record<TCommunicationTask["work_state"]["status"], string> = {
  untouched: "COMMUNICATION.WorkState_Untouched",
  in_progress: "COMMUNICATION.WorkState_InProgress",
  approved: "COMMUNICATION.WorkState_Approved",
};

const questionStatusOptions: Array<{ value: TCommunicationQuestion["status"]; label: string }> = [
  { value: "open", label: localizeByKeyStatic("COMMUNICATION.QuestionStatus_Open") },
  { value: "reviewed", label: localizeByKeyStatic("COMMUNICATION.QuestionStatus_Reviewed") },
];

const questionStatusLabelMap: Record<TCommunicationQuestion["status"], string> = {
  open: localizeByKeyStatic("COMMUNICATION.QuestionStatus_Open"),
  reviewed: localizeByKeyStatic("COMMUNICATION.QuestionStatus_Reviewed"),
};

const dismissReasonOptions: Array<{ value: TCommunicationDismissReason; label: string }> = [
  {
    value: "spam_marketing",
    label: localizeByKeyStatic("COMMUNICATION.NotRelevantReason_spam_marketing"),
  },
  {
    value: "no_project_context",
    label: localizeByKeyStatic("COMMUNICATION.NotRelevantReason_no_project_context"),
  },
  {
    value: "wrong_recipient",
    label: localizeByKeyStatic("COMMUNICATION.NotRelevantReason_wrong_recipient"),
  },
  {
    value: "informational_only",
    label: localizeByKeyStatic("COMMUNICATION.NotRelevantReason_informational_only"),
  },
];

type TReplyDraftTemplateKey = "available" | "unavailable" | "under_review";

const suggestionStatusLabelKeyMap: Record<TCommunicationSuggestionStatus, string> = {
  pending: "COMMUNICATION.EquipmentSuggestion_Pending",
  confirmed: "COMMUNICATION.EquipmentSuggestion_Confirmed",
  changed: "COMMUNICATION.EquipmentSuggestion_Changed",
};

const stepIconMap: Record<TCommunicationTaskStepIconKey, React.ElementType> = {
  check: Icons.Check,
  attention: Icons.Attention,
  note: Icons.Note,
  lock: Icons.Padlock,
};

const getCountLabel = (
  count: number,
  singularKey: string,
  pluralKey: string
): string => localizeByKeyStatic(count === 1 ? singularKey : pluralKey, { count });

const getRequestStatusTone = (status: TCommunicationRequestStatus): string => {
  if (status === "approved") {
    return "approved";
  }

  if (status === "unidentified") {
    return "warning";
  }

  if (status === "rejected" || status === "cancelled") {
    return "muted";
  }

  return "pending";
};

const CommunicationTaskForm: React.FC<ICommunicationTaskFormProps> = ({
  task,
  canApprove,
  equipmentOptions,
  flooringOptions,
  organizerOptions,
  exhibitionOptions,
  auditEntry,
  isAuditView,
  onApprove,
  onDismissNotRelevant,
  onDirtyStart,
  onDirtyChange,
  action,
  showModal,
}) => {
  const [form] = Form.useForm<TCommunicationTaskFormValues>();
  const viewportWidth = useWindowSize()?.width || 0;
  const presentationMode = useMemo(
    () => getCommunicationDrawerPresentationMode(viewportWidth),
    [viewportWidth]
  );
  const isMobileSimplified = presentationMode === "mobile-simplified";
  const isTabletCondensed = presentationMode === "tablet-condensed";
  const shouldShowStepDescriptions = !isMobileSimplified && !isTabletCondensed;
  const shouldRenderSecondaryContextRibbon = !isMobileSimplified;
  const shouldRenderDemoStrip = !isMobileSimplified;
  const shouldRenderStepOneSummary = !isMobileSimplified;
  const shouldRenderStepOneFollowUpStrip = !isMobileSimplified;
  const shouldRenderStepTwoSummary = !isMobileSimplified;
  const shouldRenderExtractedLogisticsBand = !isMobileSimplified;
  const [isDirty, setIsDirty] = useState(false);
  const hasTriggeredDirtyStartRef = useRef(false);
  const readingPaneWheelDirectionRef = useRef<"up" | "down" | null>(null);
  const canAutoExpandReadingPaneRef = useRef(false);
  const isReadingPaneScrollCondensedRef = useRef(false);
  const [currentStep, setCurrentStep] = useState<number>(getDrawerStartStep(task, isAuditView));
  const [maxUnlockedStep, setMaxUnlockedStep] = useState<TCommunicationTaskStep["id"]>(
    getInitialUnlockedStep(task, isAuditView)
  );
  const [openEquipmentField, setOpenEquipmentField] = useState<number | null>(null);
  const [isReadingPaneExpanded, setIsReadingPaneExpanded] = useState<boolean>(
    () => getDefaultReadingPaneExpanded(presentationMode, getDrawerStartStep(task, isAuditView))
  );
  const [isReadingPaneScrollCondensed, setIsReadingPaneScrollCondensed] = useState<boolean>(false);
  const [showStandUpdates, setShowStandUpdates] = useState<boolean>(
    hasStandUpdates(task.extracted_changes.stand_updates) || hasStandUpdates(task.final_changes.stand_updates)
  );
  const [showManagerNote, setShowManagerNote] = useState<boolean>(
    hasManagerNote(task.final_changes.manager_note)
  );
  const [showReplyHandoffNote, setShowReplyHandoffNote] = useState<boolean>(
    Boolean(task.final_changes.reply_handoff_note?.trim()) || task.questions.length > 0
  );
  const [isDismissModalOpen, setIsDismissModalOpen] = useState(false);
  const [dismissReason, setDismissReason] = useState<TCommunicationDismissReason>("no_project_context");
  const shouldAutoCondenseReadingPane = isReadingPaneExpanded && currentStep === 2;
  const confidenceValue = normalizeCommunicationConfidence(
    task.ai_response_confidence ?? task.confidence
  );
  const confidenceTone = getCommunicationConfidenceTone(confidenceValue);
  const equipmentIdToGroupIdMap = useMemo(
    () =>
      new Map(
        equipmentOptions.flatMap((option) => {
          const pairs: Array<[string, string]> = [[ option.value, option.value ]];

          if (option.rawId) {
            pairs.push([ option.rawId as string, option.value ]);
          }

          return pairs;
        })
      ),
    [equipmentOptions]
  );
  const initialFormValues = useMemo(
    () => mapTaskToFormValues(task, equipmentIdToGroupIdMap),
    [
      equipmentIdToGroupIdMap,
      task.id,
      task.event_name,
      task.company_name,
      task.stand_number,
      task.organizer_id,
      task.exhibition_type_id,
      task.final_changes,
      task.questions,
      task.stand_numbers,
    ]
  );
  const initialFormSnapshot = useMemo(
    () =>
      JSON.stringify(
        normalizeTaskFormSnapshot(task, initialFormValues, equipmentIdToGroupIdMap)
      ),
    [equipmentIdToGroupIdMap, initialFormValues, task]
  );

  const watchedQuestions = Form.useWatch("questions", form);
  const watchedContextGroups = Form.useWatch("context_groups", form);
  const watchedEventName = Form.useWatch("event_name", form);
  const watchedCompanyName = Form.useWatch("company_name", form);
  const watchedStandNumber = Form.useWatch("stand_number", form);
  const watchedStandNumbers = Form.useWatch("stand_numbers", form);
  const watchedEquipmentChanges = Form.useWatch("equipment_changes", form);
  const watchedStandUpdates = Form.useWatch("stand_updates", form);
  const watchedReplyHandoffNote = Form.useWatch("reply_handoff_note", form);
  const baseResolvedQuestions =
    watchedQuestions ?? form.getFieldValue("questions") ?? task.questions;
  const resolvedQuestions = useMemo(() => {
    const aiQuestionText = task.ai_question_text?.trim();

    if (!aiQuestionText) {
      return baseResolvedQuestions;
    }

    const hasExistingQuestion = baseResolvedQuestions.some((question) =>
      question?.text?.trim().toLowerCase() === aiQuestionText.toLowerCase()
    );

    if (hasExistingQuestion) {
      return baseResolvedQuestions;
    }

    return [
      ...baseResolvedQuestions,
      {
        id: `ai-question-${task.id}`,
        text: aiQuestionText,
        status: "open" as const,
      },
    ];
  }, [ baseResolvedQuestions, task.ai_question_text, task.id ]);
  const resolvedContextGroups = useMemo(
    () => normalizeContextGroups(
      watchedContextGroups
      ?? form.getFieldValue("context_groups")
      ?? task.context_groups
      ?? [{
        id: `context-${task.id}-0`,
        event_name: task.event_name || "",
        company_name: task.company_name || "",
        stand_number: task.stand_number || "",
      }],
      task.id
    ),
    [form, task.company_name, task.context_groups, task.event_name, task.id, task.stand_number, watchedContextGroups]
  );
  const primaryContextGroup = getPrimaryContextGroup(resolvedContextGroups);
  const resolvedEventName =
    primaryContextGroup?.event_name
    || watchedEventName
    || form.getFieldValue("event_name")
    || task.event_name
    || "";
  const resolvedCompanyName =
    primaryContextGroup?.company_name
    || watchedCompanyName
    || form.getFieldValue("company_name")
    || task.company_name
    || "";
  const resolvedStandNumber =
    primaryContextGroup?.stand_number
    || watchedStandNumber
    || form.getFieldValue("stand_number")
    || task.stand_number
    || "";
  const resolvedStandNumbers = useMemo(() => {
    const sourceStandNumbers =
      resolvedContextGroups.length > 0
      ? resolvedContextGroups.map((group) => group.stand_number)
      : watchedStandNumbers
      ?? form.getFieldValue("stand_numbers")
      ?? task.stand_numbers
      ?? (task.stand_number ? [ task.stand_number ] : [ "" ]);

    return Array.isArray(sourceStandNumbers) && sourceStandNumbers.length > 0
      ? sourceStandNumbers.map((value) => (typeof value === "string" ? value : ""))
      : [ "" ];
  }, [form, resolvedContextGroups, task.stand_number, task.stand_numbers, watchedStandNumbers]);
  const normalizedStandNumbers = useMemo(
    () => normalizeStandNumbers(resolvedStandNumbers),
    [resolvedStandNumbers]
  );
  const primaryStandNumber =
    primaryContextGroup?.stand_number || normalizedStandNumbers[0] || resolvedStandNumber || task.stand_number || "";
  const hasBlankStandNumbers = resolvedContextGroups.some((group) => !group.stand_number?.trim())
    || resolvedStandNumbers.some((value) => !value?.trim());
  const sourceEquipmentChanges = useMemo(
    () =>
      watchedEquipmentChanges
      ?? form.getFieldValue("equipment_changes")
      ?? task.final_changes.equipment_changes,
    [form, task.final_changes.equipment_changes, watchedEquipmentChanges]
  );
  const visibleEquipmentChangeEntries = useMemo(
    () =>
      (sourceEquipmentChanges || [])
        .map((change, index) => ({ change, index }))
        .filter(({ change }) => isEquipmentChangeVisibleForContexts(change, resolvedContextGroups)),
    [resolvedContextGroups, sourceEquipmentChanges]
  );
  const resolvedEquipmentChanges = useMemo(
    () => visibleEquipmentChangeEntries.map(({ change }) => change),
    [visibleEquipmentChangeEntries]
  );
  const resolvedStandUpdates =
    watchedStandUpdates
    ?? form.getFieldValue("stand_updates")
    ?? task.final_changes.stand_updates
    ?? {};
  const resolvedReplyHandoffNote =
    watchedReplyHandoffNote
    ?? form.getFieldValue("reply_handoff_note")
    ?? task.final_changes.reply_handoff_note
    ?? "";
  const isFinalizedTask = Boolean(isAuditView || task.status === CommunicationConst.statuses.APPROVED);
  const isDismissedHistoryEntry = Boolean(isAuditView && auditEntry?.outcome === "dismissed");
  const isAiFallbackTask = task.request_source === "ai_fallback";
  const aiSourceLabelKey = isAiFallbackTask
    ? "COMMUNICATION.Source_AiFallback"
    : task.ai_selected_response_id
      ? "COMMUNICATION.Source_AiSourced"
      : undefined;
  const aiParserLabel =
    task.ai_provider && task.ai_model
      ? `${task.ai_provider} / ${task.ai_model}`
      : task.ai_model || task.ai_provider || "";
  const requestGroups = task.request_groups;
  const requestItemsCount = task.request_items.length;
  const workStateLabel = localizeByKeyStatic(workStateLabelKeyMap[task.work_state.status]);
  const dismissedReasonLabel =
    isDismissedHistoryEntry && auditEntry?.dismissed_reason
      ? localizeByKeyStatic(`COMMUNICATION.NotRelevantReason_${auditEntry.dismissed_reason}`)
      : "";
  const workStateTouchedLabel = task.work_state.touched_at
    ? localizeByKeyStatic("COMMUNICATION.WorkState_LastTouched", {
      date: formatCommunicationDateTime(task.work_state.touched_at),
    })
    : "";
  const hasExistingEquipmentChanges = Boolean(
    task.extracted_changes.equipment_changes.length || task.final_changes.equipment_changes.length
  );
  const hasSpamSignal = task.intent === "spam";
  const requestGroupsHintKey = isAiFallbackTask
    ? "COMMUNICATION.Section_RequestGroupsHintFallback"
    : "COMMUNICATION.Section_RequestGroupsHint";
  const extractedChangesHintKey = isAiFallbackTask
    ? "COMMUNICATION.Preview_LogisticsExtractedHintFallback"
    : "COMMUNICATION.Preview_LogisticsExtractedHint";
  const openQuestionsCount = resolvedQuestions.filter((question) => question?.status === "open").length;
  const pendingEquipmentSuggestionsCount = resolvedEquipmentChanges.filter((change) => {
    const suggestionMeta = getEquipmentSuggestionMeta(change);
    return suggestionMeta?.status === "pending";
  }).length;
  const unresolvedEquipmentSelectionCount = resolvedEquipmentChanges.filter((change, index, changes) => {
    if (shouldIgnoreEquipmentChangeValidation(change, index, changes, hasExistingEquipmentChanges)) {
      return false;
    }

    return !getEffectiveEquipmentGroupId(change);
  }).length;
  const hasStepTwoBlockingIssues = resolvedEquipmentChanges.some((change, index, changes) => {
    if (shouldIgnoreEquipmentChangeValidation(change, index, changes, hasExistingEquipmentChanges)) {
      return false;
    }

    return !getEffectiveEquipmentGroupId(change) || !change?.qty || change.qty < 1;
  });
  const hasReviewableEquipmentChanges = resolvedEquipmentChanges.some((change, index, changes) => {
    if (shouldIgnoreEquipmentChangeValidation(change, index, changes, hasExistingEquipmentChanges)) {
      return false;
    }

    return !isBlankEquipmentChange(change);
  });
  const reviewChangesCount = pendingEquipmentSuggestionsCount + unresolvedEquipmentSelectionCount;
  const stepTwoReviewCount = reviewChangesCount + openQuestionsCount;
  const reviewedQuestionsCount = resolvedQuestions.filter((question) => question?.status === "reviewed").length;
  const isMatchComplete = areContextGroupsComplete(resolvedContextGroups);
  const hasDismissReviewSignals = Boolean(
    requestItemsCount > 0
    || hasReviewableEquipmentChanges
    || resolvedQuestions.length > 0
  );
  const shouldShowMarkAsNotRelevant = Boolean(
    currentStep === 1
    && !isAuditView
    && onDismissNotRelevant
    && !isMatchComplete
  );
  const matchingMissingFieldLabels = useMemo(() => {
    const missingFields: string[] = [];

    if (resolvedContextGroups.some((group) => !group.event_name?.trim())) {
      missingFields.push(localizeByKeyStatic("FORMS.Input_Label_EventName"));
    }

    if (resolvedContextGroups.some((group) => !group.company_name?.trim())) {
      missingFields.push(localizeByKeyStatic("FORMS.Input_Label_CompanyName"));
    }

    if (resolvedContextGroups.some((group) => !group.stand_number?.trim()) || normalizedStandNumbers.length === 0 || hasBlankStandNumbers) {
      missingFields.push(localizeByKeyStatic("COMMUNICATION.Field_StandNumbers"));
    }

    return missingFields;
  }, [
    resolvedContextGroups,
    hasBlankStandNumbers,
    normalizedStandNumbers.length,
  ]);

  useEffect(() => {
    if (showModal === false) {
      return;
    }

    hasTriggeredDirtyStartRef.current = false;
    setIsDirty(false);
    onDirtyChange?.(false);
    form.resetFields();
    form.setFieldsValue(initialFormValues);
    const startStep = getDrawerStartStep(task, isAuditView);
    setCurrentStep(startStep);
    setMaxUnlockedStep(getInitialUnlockedStep(task, isAuditView));
    setOpenEquipmentField(null);
    readingPaneWheelDirectionRef.current = null;
    canAutoExpandReadingPaneRef.current = false;
    isReadingPaneScrollCondensedRef.current = false;
    setIsReadingPaneExpanded(getDefaultReadingPaneExpanded(presentationMode, startStep));
    setIsReadingPaneScrollCondensed(false);
    setShowStandUpdates(
      hasStandUpdates(task.extracted_changes.stand_updates)
      || hasStandUpdates(task.final_changes.stand_updates)
    );
    setShowManagerNote(hasManagerNote(task.final_changes.manager_note));
    setShowReplyHandoffNote(
      Boolean(task.final_changes.reply_handoff_note?.trim()) || task.questions.length > 0
    );
  }, [ form, initialFormValues, isAuditView, onDirtyChange, presentationMode, showModal, task.id ]);

  useEffect(() => {
    if (!showModal || typeof document === "undefined" || !shouldAutoCondenseReadingPane) {
      setIsReadingPaneScrollCondensed(false);
      return undefined;
    }

    const drawerBody = document.querySelector(".communication-drawer .ant-drawer-body") as HTMLElement | null;

    if (!drawerBody) {
      setIsReadingPaneScrollCondensed(false);
      return undefined;
    }

    const condenseThreshold = 56;
    const expandThreshold = 4;
    const handleWheelIntent = (event: WheelEvent) => {
      if (event.deltaY > 0) {
        readingPaneWheelDirectionRef.current = "down";
        canAutoExpandReadingPaneRef.current = false;
        return;
      }

      if (event.deltaY < 0) {
        readingPaneWheelDirectionRef.current = "up";

        if (isReadingPaneScrollCondensedRef.current) {
          canAutoExpandReadingPaneRef.current = true;
        }
      }
    };

    const handleDrawerScroll = () => {
      const nextScrollTop = drawerBody.scrollTop;

      setIsReadingPaneScrollCondensed((currentValue) => {
        if (nextScrollTop >= condenseThreshold) {
          if (!currentValue) {
            canAutoExpandReadingPaneRef.current = false;
          }

          isReadingPaneScrollCondensedRef.current = true;
          return true;
        }

        if (
          currentValue
          && nextScrollTop <= expandThreshold
          && canAutoExpandReadingPaneRef.current
          && readingPaneWheelDirectionRef.current === "up"
        ) {
          canAutoExpandReadingPaneRef.current = false;
          isReadingPaneScrollCondensedRef.current = false;
          return false;
        }

        isReadingPaneScrollCondensedRef.current = currentValue;
        return currentValue;
      });
    };

    handleDrawerScroll();
    drawerBody.addEventListener("wheel", handleWheelIntent, { passive: true });
    drawerBody.addEventListener("scroll", handleDrawerScroll, { passive: true });

    return () => {
      drawerBody.removeEventListener("wheel", handleWheelIntent);
      drawerBody.removeEventListener("scroll", handleDrawerScroll);
    };
  }, [showModal, shouldAutoCondenseReadingPane, task.id]);

  useEffect(() => {
    if (!showModal || typeof document === "undefined") {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      const drawerBody = document.querySelector(".communication-drawer .ant-drawer-body") as HTMLElement | null;

      if (!drawerBody) {
        return;
      }

      drawerBody.scrollTo({ top: 0, behavior: "auto" });
      readingPaneWheelDirectionRef.current = null;
      canAutoExpandReadingPaneRef.current = false;
      isReadingPaneScrollCondensedRef.current = false;
      setIsReadingPaneScrollCondensed(false);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [currentStep, showModal, task.id]);

  useEffect(() => {
    if (showModal === false) {
      return;
    }

    readingPaneWheelDirectionRef.current = null;
    canAutoExpandReadingPaneRef.current = false;
    isReadingPaneScrollCondensedRef.current = false;
    setIsReadingPaneExpanded(getDefaultReadingPaneExpanded(presentationMode, currentStep));
    setIsReadingPaneScrollCondensed(false);
  }, [ currentStep, presentationMode, showModal ]);

  useEffect(() => {
    if (showModal === false) {
      return;
    }

    form.setFieldsValue({
      event_name: resolvedEventName,
      company_name: resolvedCompanyName,
      stand_number: primaryStandNumber,
      stand_numbers: normalizedStandNumbers.length > 0 ? normalizedStandNumbers : [ "" ],
    });
  }, [
    form,
    normalizedStandNumbers,
    primaryStandNumber,
    resolvedCompanyName,
    resolvedEventName,
    showModal,
  ]);

  useEffect(() => {
    if (showModal === false) {
      return;
    }

    const currentPrimaryStand = form.getFieldValue("stand_number") || "";

    if (currentPrimaryStand !== primaryStandNumber) {
      form.setFieldValue("stand_number", primaryStandNumber);
    }
  }, [form, primaryStandNumber, showModal]);

  useEffect(() => {
    if (isFinalizedTask) {
      if (maxUnlockedStep !== 3) {
        setMaxUnlockedStep(3);
      }

      return;
    }

    let nextUnlockedStep = maxUnlockedStep;

    if (!isMatchComplete) {
      nextUnlockedStep = 1;
    } else if (hasStepTwoBlockingIssues && nextUnlockedStep > 2) {
      nextUnlockedStep = 2;
    }

    if (nextUnlockedStep !== maxUnlockedStep) {
      setMaxUnlockedStep(nextUnlockedStep);
    }

    if (currentStep > nextUnlockedStep) {
      setCurrentStep(nextUnlockedStep);
    }
  }, [
    currentStep,
    hasStepTwoBlockingIssues,
    isFinalizedTask,
    isMatchComplete,
    maxUnlockedStep,
  ]);

  useEffect(() => {
    if (showModal === false || !equipmentIdToGroupIdMap.size) {
      return;
    }

    const currentChanges = form.getFieldValue("equipment_changes") || [];
    let hasHydratedEquipmentMetadata = false;

    const hydratedChanges = currentChanges.map((change: TCommunicationTaskFormValues["equipment_changes"][number]) => {
      if (!change?.equipment_id) {
        const nextSuggestedGroupId = change?.suggested_equipment_group_id;
        const nextEquipmentGroupId =
          change?.equipment_group_id
          || (change?.suggestion_status !== "changed" ? nextSuggestedGroupId : undefined);

        if (
          nextEquipmentGroupId === change?.equipment_group_id
          && nextSuggestedGroupId === change?.suggested_equipment_group_id
          && change?.suggestion_status
        ) {
          return change;
        }

        hasHydratedEquipmentMetadata = true;

        return {
          ...change,
          equipment_group_id: nextEquipmentGroupId,
          suggested_equipment_group_id: nextSuggestedGroupId,
          suggestion_status:
            nextSuggestedGroupId
              ? change?.suggestion_status || "pending"
              : change?.suggestion_status,
        };
      }

      const resolvedGroupId = equipmentIdToGroupIdMap.get(change.equipment_id);
      const nextSuggestedGroupId =
        change.suggested_equipment_group_id || resolvedGroupId;
      const nextEquipmentGroupId =
        change.equipment_group_id
        || (change.suggestion_status !== "changed" ? nextSuggestedGroupId : undefined);

      if (!nextEquipmentGroupId && !nextSuggestedGroupId) {
        return change;
      }

      if (
        nextEquipmentGroupId === change.equipment_group_id
        && nextSuggestedGroupId === change.suggested_equipment_group_id
        && change.suggestion_status
      ) {
        return change;
      }

      hasHydratedEquipmentMetadata = true;

      return {
        ...change,
        equipment_group_id: nextEquipmentGroupId,
        suggested_equipment_group_id: nextSuggestedGroupId,
        suggestion_status:
          nextSuggestedGroupId
            ? change.suggestion_status || "pending"
            : change.suggestion_status,
      };
    });

    if (hasHydratedEquipmentMetadata) {
      form.setFieldValue("equipment_changes", hydratedChanges);
    }
  }, [equipmentIdToGroupIdMap, form, showModal]);

  useEffect(() => {
    const nextMergedEquipmentOptions = buildMergedEquipmentOptions(
      equipmentOptions,
      task.final_changes.equipment_changes,
      equipmentIdToGroupIdMap
    );

    if (showModal === false || !nextMergedEquipmentOptions.length) {
      return;
    }

    const optionMap = new Map(
      nextMergedEquipmentOptions.map((option) => [ option.value, option.label ])
    );
    const currentChanges = form.getFieldValue("equipment_changes") || [];
    let hasPatchedSuggestions = false;

    const patchedChanges = currentChanges.map((change: TCommunicationTaskFormValues["equipment_changes"][number]) => {
      if (!change) {
        return change;
      }

      const shouldAutofillSuggestion = change.suggestion_status !== "changed";
      const nextEquipmentGroupId =
        change.equipment_group_id
        || (shouldAutofillSuggestion ? change.suggested_equipment_group_id : undefined);
      const nextItemLabel =
        change.item_label || (nextEquipmentGroupId ? optionMap.get(nextEquipmentGroupId) || "" : "");

      if (
        nextEquipmentGroupId === change.equipment_group_id
        && nextItemLabel === change.item_label
      ) {
        return change;
      }

      hasPatchedSuggestions = true;

      return {
        ...change,
        equipment_group_id: nextEquipmentGroupId,
        item_label: nextItemLabel,
      };
    });

    if (hasPatchedSuggestions) {
      form.setFieldValue("equipment_changes", patchedChanges);
    }
  }, [equipmentIdToGroupIdMap, equipmentOptions, form, showModal, task.final_changes.equipment_changes]);

  const steps = useMemo(
    () =>
      buildCommunicationTaskSteps({
        hasReviewableChanges: hasReviewableEquipmentChanges,
        isFinalized: isFinalizedTask,
        isMatchComplete,
        maxUnlockedStep,
        stepTwoReviewCount,
        openQuestionsCount,
      }),
    [
      hasReviewableEquipmentChanges,
      isFinalizedTask,
      isMatchComplete,
      maxUnlockedStep,
      openQuestionsCount,
      stepTwoReviewCount,
    ]
  );

  const displayRequestGroups = useMemo(
    () => {
      const contextGroupMap = new Map(
        resolvedContextGroups.map((group) => [ group.id, group ])
      );

      return requestGroups.flatMap((group) => {
        const matchedContextGroup =
          contextGroupMap.get(group.id)
          || resolvedContextGroups.find((contextGroup) =>
            contextGroup.stand_number.trim() === group.stand_number.trim()
            && contextGroup.company_name.trim() === group.company_name.trim()
            && contextGroup.event_name.trim() === group.event_name.trim()
          );

        if (!matchedContextGroup) {
          return [];
        }

        return {
          ...group,
          stand_number: matchedContextGroup?.stand_number?.trim() || group.stand_number,
          company_name: matchedContextGroup?.company_name?.trim() || group.company_name,
          event_name: matchedContextGroup?.event_name?.trim() || group.event_name,
        };
      });
    },
    [requestGroups, resolvedContextGroups]
  );
  const visibleRequestItemsCount = useMemo(
    () => displayRequestGroups.reduce((sum, group) => sum + group.requests.length, 0),
    [displayRequestGroups]
  );
  const requestGroupCount = displayRequestGroups.length;
  const hasMultipleRequestGroups = requestGroupCount > 1;
  const excludedContextGroups = useMemo(
    () => getExcludedContextGroupsFromRequestGroups(task.request_groups, resolvedContextGroups),
    [resolvedContextGroups, task.request_groups]
  );
  const followUpRequestGroup =
    displayRequestGroups.find((group) => group.question_count > 0)
    || displayRequestGroups.find((group) => group.unidentified_count > 0)
    || displayRequestGroups[0];
  const followUpStandNumber = followUpRequestGroup?.stand_number || primaryStandNumber || "";

  const mergedEquipmentOptions = useMemo(
    () =>
      buildMergedEquipmentOptions(
        equipmentOptions,
        task.final_changes.equipment_changes,
        equipmentIdToGroupIdMap
      ),
    [ equipmentIdToGroupIdMap, equipmentOptions, task.final_changes.equipment_changes ]
  );

  const equipmentOptionsMap = useMemo(
    () => new Map(mergedEquipmentOptions.map((option) => [ option.value, option.label ])),
    [mergedEquipmentOptions]
  );

  const equipmentSelectOptions = useMemo(
    () =>
      mergedEquipmentOptions.map((option) => ({
        ...option,
        searchLabel: option.label,
      })),
    [mergedEquipmentOptions]
  );
  const standSelectOptions = useMemo(
    () =>
      Array.from(
        new Set(
          [
            ...resolvedStandNumbers,
            ...resolvedContextGroups.map((group) => group.stand_number).filter(Boolean),
            ...displayRequestGroups.map((group) => group.stand_number).filter(Boolean),
            ...resolvedEquipmentChanges.map((change) => change?.stand_number || "").filter(Boolean),
            task.stand_number || "",
          ].filter(Boolean)
        )
      ).map((standNumber) => ({
        value: standNumber,
        label: standNumber,
      })),
    [
      resolvedEquipmentChanges,
      displayRequestGroups,
      resolvedContextGroups,
      resolvedStandNumbers,
      task.stand_number,
    ]
  );
  const editableChangeGroups = useMemo<TEditableChangeGroup[]>(() => {
    const orderMap = new Map(
      standSelectOptions.map((option, index) => [ option.value.trim(), index ])
    );
    const groupsMap = new Map<string, TEditableChangeGroup>();

    visibleEquipmentChangeEntries.forEach(({ change, index }) => {
      const standNumber = getEditableChangeStandNumber(change);
      const matchedContextGroup = findMatchingContextGroup(change, resolvedContextGroups);
      const eventName =
        matchedContextGroup?.event_name
        || (typeof change?.event_name === "string" ? change.event_name.trim() : "")
        || undefined;
      const companyName =
        matchedContextGroup?.company_name
        || (typeof change?.company_name === "string" ? change.company_name.trim() : "")
        || undefined;
      const groupKey =
        matchedContextGroup?.id
        || buildContextGroupLookupKey(eventName, companyName, standNumber)
        || "__unassigned__";
      const existingGroup = groupsMap.get(groupKey);

      if (existingGroup) {
        existingGroup.itemIndexes.push(index);
        return;
      }

      groupsMap.set(groupKey, {
        key: groupKey,
        standNumber: standNumber || undefined,
        eventName,
        companyName,
        itemIndexes: [ index ],
      });
    });

    return Array.from(groupsMap.values()).sort((left, right) => {
      const leftOrder = left.standNumber ? orderMap.get(left.standNumber) ?? Number.MAX_SAFE_INTEGER : Number.MAX_SAFE_INTEGER;
      const rightOrder = right.standNumber ? orderMap.get(right.standNumber) ?? Number.MAX_SAFE_INTEGER : Number.MAX_SAFE_INTEGER;

      if (leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }

      return (left.standNumber || "").localeCompare(right.standNumber || "");
    });
  }, [ resolvedContextGroups, standSelectOptions, visibleEquipmentChangeEntries ]);
  const hasMultiStandEditing = resolvedContextGroups.length > 1 || editableChangeGroups.length > 1 || standSelectOptions.length > 1;

  const finalPreviewEquipmentChanges = useMemo(() => {
    const watchedChanges = watchedEquipmentChanges || [];
    const formChanges =
      (form.getFieldValue("equipment_changes") || []) as TCommunicationTaskFormValues["equipment_changes"];
    const sourceChanges =
      watchedChanges.length
        ? watchedChanges
        : formChanges.length
          ? formChanges
          : task.final_changes.equipment_changes;

    return sourceChanges
      .filter((change) => isEquipmentChangeVisibleForContexts(change, resolvedContextGroups))
      .map((change) => {
        const matchedContextGroup = findMatchingContextGroup(change, resolvedContextGroups);
        const resolvedEquipmentGroupId =
          change?.equipment_group_id || change?.suggested_equipment_group_id || undefined;
        const nextItemLabel =
          (resolvedEquipmentGroupId
            ? equipmentOptionsMap.get(resolvedEquipmentGroupId) || ""
            : "")
          || change?.item_label?.trim()
          || change?.equipment_title
          || "";

        return {
          ...change,
          event_name: change?.event_name || matchedContextGroup?.event_name || resolvedEventName || "",
          company_name: change?.company_name || matchedContextGroup?.company_name || resolvedCompanyName || "",
          stand_number: change?.stand_number || matchedContextGroup?.stand_number || primaryStandNumber || "",
          equipment_group_id: resolvedEquipmentGroupId,
          item_label: nextItemLabel,
        };
      })
      .filter((change) => change?.item_label?.trim());
  }, [
    currentStep,
    equipmentOptionsMap,
    form,
    resolvedCompanyName,
    resolvedContextGroups,
    resolvedEventName,
    primaryStandNumber,
    showModal,
    task.final_changes.equipment_changes,
    watchedEquipmentChanges,
  ]);

  const originalEmailEquipmentChanges = useMemo(
    () => [
      ...task.extracted_changes.equipment_changes.filter((change) =>
        isEquipmentChangeVisibleForContexts(change, resolvedContextGroups)
      ),
      ...finalPreviewEquipmentChanges,
    ],
    [ finalPreviewEquipmentChanges, resolvedContextGroups, task.extracted_changes.equipment_changes ]
  );
  const originalEmailSections = useMemo(
    () => buildCommunicationOriginalEmailSections(task.original_body),
    [task.original_body]
  );
  const originalEmailSummary = useMemo(() => {
    const contentSections = originalEmailSections.filter((section) => section.type !== "header");
    const summarySource = contentSections.slice(0, 2).map((section) => section.text.trim()).filter(Boolean).join(" ");

    return task.ai_summary || summarySource || task.subject || "\u2014";
  }, [originalEmailSections, task.ai_summary, task.subject]);
  const dismissedEntrySummary = task.ai_summary || originalEmailSummary;

  const finalPreviewSource = useMemo(
    () => ({
      event_name: resolvedEventName,
      company_name: resolvedCompanyName,
      stand_number: primaryStandNumber,
      received_at: task.received_at,
      changes: {
        equipment_changes: finalPreviewEquipmentChanges,
        stand_updates: resolvedStandUpdates,
        manager_note: "",
      },
    }),
    [
      finalPreviewEquipmentChanges,
      resolvedCompanyName,
      resolvedEventName,
      primaryStandNumber,
      resolvedStandUpdates,
      task.company_name,
      task.event_name,
      task.received_at,
      task.stand_number,
    ]
  );

  const extractedPreviewSource = useMemo(
    () => ({
      event_name: resolvedEventName,
      company_name: resolvedCompanyName,
      stand_number: primaryStandNumber,
      received_at: task.received_at,
      changes: {
        ...task.extracted_changes,
        equipment_changes: task.extracted_changes.equipment_changes.filter((change) =>
          isEquipmentChangeVisibleForContexts(change, resolvedContextGroups)
        ),
      },
    }),
    [
      resolvedCompanyName,
      resolvedContextGroups,
      resolvedEventName,
      primaryStandNumber,
      task.company_name,
      task.event_name,
      task.extracted_changes,
      task.received_at,
      task.stand_number,
    ]
  );

  const extractedLogisticsPreview = useMemo(
    () => buildCommunicationLogisticsPreview(extractedPreviewSource),
    [extractedPreviewSource]
  );

  const finalLogisticsPreview = useMemo(
    () => buildCommunicationLogisticsPreview(finalPreviewSource),
    [finalPreviewSource]
  );
  const stepTwoStandGroupCount =
    requestGroupCount || normalizedStandNumbers.length || (primaryStandNumber ? 1 : 0);
  const parsedStandCount = requestGroupCount || normalizedStandNumbers.length || (primaryStandNumber ? 1 : 0);
  const shouldColorizeStandGroups = stepTwoStandGroupCount > 1;
  const standToneStyleMap = useMemo(() => {
    const uniqueStandNumbers = Array.from(
      new Set(
        [
          ...displayRequestGroups.map((group) => group.stand_number),
          ...editableChangeGroups.map((group) => group.standNumber || ""),
          ...normalizedStandNumbers,
          task.stand_number || "",
        ]
          .map((value) => (typeof value === "string" ? value.trim() : ""))
          .filter(Boolean)
      )
    );

    return new Map(
      uniqueStandNumbers.map((standNumber, index) => [ standNumber, buildStandToneStyle(index) ])
    );
  }, [ displayRequestGroups, editableChangeGroups, normalizedStandNumbers, task.stand_number ]);
  const getStandToneProps = (
    standNumber?: string | null,
    enabled = true
  ): {
    toneClassName: string;
    surfaceClassName: string;
    style?: React.CSSProperties;
  } => {
    const normalizedStandNumber = typeof standNumber === "string" ? standNumber.trim() : "";
    const style = normalizedStandNumber
      ? enabled
        ? standToneStyleMap.get(normalizedStandNumber)
        : buildNeutralStandToneStyle()
      : undefined;

    return {
      toneClassName: style ? "communication-task-form__stand-tone" : "",
      surfaceClassName: style ? "communication-task-form__stand-surface" : "",
      style,
    };
  };
  const unresolvedReviewCount = pendingEquipmentSuggestionsCount + unresolvedEquipmentSelectionCount;
  const totalClarificationCount = resolvedQuestions.length;
  const contextStandLabels = normalizedStandNumbers
    .map((standNumber) => formatCanonicalStandNumberLabel(standNumber))
    .filter((standNumber) => standNumber !== "\u2014");
  const standsSummaryLabel =
    contextStandLabels.length > 0
      ? contextStandLabels.join(" • ")
      : primaryStandNumber
        ? formatCanonicalStandNumberLabel(primaryStandNumber)
        : "\u2014";
  const isLastStep = currentStep === steps.length;
  const stepTwoSummaryItems = useMemo(
    () => [
      {
        key: "changes",
        labelKey: "COMMUNICATION.Step2Summary_ChangesLabel",
        value: reviewChangesCount,
        caption: getCountLabel(
          finalPreviewEquipmentChanges.length,
          "COMMUNICATION.RequestGroup_ItemSingle",
          "COMMUNICATION.RequestGroup_Items"
        ),
        tone: reviewChangesCount > 0 ? "warning" : "success",
      },
      {
        key: "clarifications",
        labelKey: "COMMUNICATION.Step2Summary_ClarificationsLabel",
        value: openQuestionsCount,
        caption:
          resolvedQuestions.length > 0
            ? getCountLabel(
              resolvedQuestions.length,
              "COMMUNICATION.RequestGroup_QuestionSingle",
              "COMMUNICATION.RequestGroup_Questions"
            )
            : localizeByKeyStatic("COMMUNICATION.Text_NoQuestions"),
        tone: openQuestionsCount > 0 ? "warning" : "default",
      },
      {
        key: "stands",
        labelKey: "COMMUNICATION.Step2Summary_StandsLabel",
        value: stepTwoStandGroupCount,
        caption: getCountLabel(
          visibleRequestItemsCount,
          "COMMUNICATION.RequestGroup_ParsedItemSingle",
          "COMMUNICATION.RequestGroup_ParsedItems"
        ),
        tone: hasMultipleRequestGroups ? "info" : "default",
      },
    ],
    [
      finalPreviewEquipmentChanges.length,
      hasMultipleRequestGroups,
      openQuestionsCount,
      resolvedQuestions.length,
      reviewChangesCount,
      stepTwoStandGroupCount,
      visibleRequestItemsCount,
    ]
  );
  const stepOneSummaryItems = useMemo(
    () => [
      {
        key: "stands",
        labelKey: "COMMUNICATION.Step1Summary_StandsLabel",
        value: parsedStandCount,
        caption: getCountLabel(
          parsedStandCount,
          "COMMUNICATION.RequestGroup_GroupSingle",
          "COMMUNICATION.RequestGroup_Groups"
        ),
        tone: parsedStandCount > 1 ? "info" : "default",
      },
      {
        key: "confidence",
        labelKey: "COMMUNICATION.Step1Summary_ParserConfidenceLabel",
        value: `${confidenceValue}%`,
        caption: localizeByKeyStatic(
          confidenceTone === "high"
            ? "COMMUNICATION.Confidence_High"
            : confidenceTone === "medium"
              ? "COMMUNICATION.Confidence_Medium"
              : "COMMUNICATION.Confidence_Low"
        ),
        tone: confidenceTone === "high" ? "success" : confidenceTone === "medium" ? "warning" : "default",
      },
      {
        key: "unresolved",
        labelKey: "COMMUNICATION.Step1Summary_UnresolvedLabel",
        value: unresolvedReviewCount,
        caption:
          unresolvedReviewCount > 0
            ? localizeByKeyStatic("COMMUNICATION.Step1Summary_UnresolvedHint", {
              count: unresolvedReviewCount,
            })
            : localizeByKeyStatic("COMMUNICATION.Step1Summary_UnresolvedClear"),
        tone: unresolvedReviewCount > 0 ? "warning" : "success",
      },
      {
        key: "clarifications",
        labelKey: "COMMUNICATION.Step1Summary_ClarificationsLabel",
        value: totalClarificationCount,
        caption:
          totalClarificationCount > 0
            ? getCountLabel(
              totalClarificationCount,
              "COMMUNICATION.RequestGroup_QuestionSingle",
              "COMMUNICATION.RequestGroup_Questions"
            )
            : localizeByKeyStatic("COMMUNICATION.Text_NoQuestions"),
        tone: totalClarificationCount > 0 ? "warning" : "default",
      },
    ],
    [
      confidenceTone,
      confidenceValue,
      parsedStandCount,
      totalClarificationCount,
      unresolvedReviewCount,
    ]
  );
  const clarificationStandContextLabel = useMemo(() => {
    const formattedStandNumbers = normalizedStandNumbers
      .map((standNumber) => formatCanonicalStandNumberLabel(standNumber))
      .filter((standNumber) => standNumber !== "\u2014");

    if (formattedStandNumbers.length === 1) {
      return localizeByKeyStatic("COMMUNICATION.QuestionCard_RelatedStand", {
        stand: formattedStandNumbers[0],
      });
    }

    if (formattedStandNumbers.length > 1) {
      return localizeByKeyStatic("COMMUNICATION.QuestionCard_RelatedStands", {
        count: formattedStandNumbers.length,
      });
    }

    return "";
  }, [normalizedStandNumbers]);
  const hasReplyHandoffNote = Boolean(resolvedReplyHandoffNote?.trim());
  const shouldShowReplyHandoffNote = showReplyHandoffNote || totalClarificationCount > 0 || hasReplyHandoffNote;
  const needsCustomerFollowUp = totalClarificationCount > 0 || hasReplyHandoffNote;
  const nextActionLabelKey = needsCustomerFollowUp
    ? "COMMUNICATION.NextAction_FollowUpNeeded"
    : "COMMUNICATION.NextAction_NoFollowUpNeeded";
  const replyDraftRecipient = typeof task.sender_email === "string" ? task.sender_email.trim() : "";
  const senderDisplayLabel = replyDraftRecipient || task.sender || "";
  const replyDraftSubject = useMemo(() => {
    const normalizedSubject = task.subject?.trim() || "";

    if (!normalizedSubject) {
      return "";
    }

    return /^re:/iu.test(normalizedSubject) ? normalizedSubject : `Re: ${normalizedSubject}`;
  }, [task.subject]);
  const replyDraftRecipientLabel = replyDraftRecipient
    || localizeByKeyStatic("COMMUNICATION.ReplyDraft_RecipientMissing");
  const replyDraftMailtoHref = useMemo(() => {
    if (!replyDraftRecipient) {
      return "";
    }

    const params = new URLSearchParams();

    if (replyDraftSubject) {
      params.set("subject", replyDraftSubject);
    }

    if (resolvedReplyHandoffNote?.trim()) {
      params.set("body", resolvedReplyHandoffNote.trim());
    }

    const queryString = params.toString();

    return `mailto:${replyDraftRecipient}${queryString ? `?${queryString}` : ""}`;
  }, [replyDraftRecipient, replyDraftSubject, resolvedReplyHandoffNote]);
  const replyDraftTemplates = useMemo(
    () => [
      {
        key: "available" as const,
        labelKey: "COMMUNICATION.ReplyDraft_TemplateAvailable",
      },
      {
        key: "unavailable" as const,
        labelKey: "COMMUNICATION.ReplyDraft_TemplateUnavailable",
      },
      {
        key: "under_review" as const,
        labelKey: "COMMUNICATION.ReplyDraft_TemplateUnderReview",
      },
    ],
    []
  );

  const getStepBadgeLabel = (step: TCommunicationTaskStep): string =>
    localizeByKeyStatic(
      step.badgeTextKey,
      step.count > 0 ? { count: step.count } : undefined
    );

  const getStepTitle = (step: TCommunicationTaskStep): string =>
    stripStepTitleIndex(localizeByKeyStatic(step.titleKey));

  const getStepDescription = (step: TCommunicationTaskStep): string =>
    localizeByKeyStatic(step.descriptionKey);

  const renderStepIcon = (step: TCommunicationTaskStep) => {
    const StepIcon = stepIconMap[step.icon];

    return <StepIcon />;
  };

  const applyReplyDraftTemplate = (templateKey: TReplyDraftTemplateKey) => {
    const labelKeyMap: Record<TReplyDraftTemplateKey, string> = {
      available: "COMMUNICATION.ReplyDraft_TextAvailable",
      unavailable: "COMMUNICATION.ReplyDraft_TextUnavailable",
      under_review: "COMMUNICATION.ReplyDraft_TextUnderReview",
    };

    form.setFieldValue("reply_handoff_note", localizeByKeyStatic(labelKeyMap[templateKey]));
    setShowReplyHandoffNote(true);
  };

  const handleOpenReplyDraft = () => {
    if (!replyDraftMailtoHref || typeof window === "undefined") {
      return;
    }

    window.location.href = replyDraftMailtoHref;
  };

  const scrollToFirstFieldError = (error: TValidateFieldsError) => {
    const firstField = error?.errorFields?.[0];

    if (firstField?.name) {
      form.scrollToField(firstField.name, { block: "center" });
    }

    if (typeof document === "undefined" || typeof window === "undefined") {
      return;
    }

    window.requestAnimationFrame(() => {
      const drawerBody = document.querySelector(".communication-drawer .ant-drawer-body") as HTMLElement | null;
      const formRoot = document.querySelector(".communication-drawer .communication-task-form") as HTMLElement | null;
      const stickyShell =
        formRoot?.querySelector(".communication-task-form__sticky-shell") as HTMLElement | null;
      const firstErroredItem =
        formRoot?.querySelector(".ant-form-item-has-error") as HTMLElement | null;

      if (!drawerBody || !formRoot || !firstErroredItem) {
        return;
      }

      const stickyOffset = (stickyShell?.offsetHeight || 0) + 24;
      const drawerRect = drawerBody.getBoundingClientRect();
      const targetRect = firstErroredItem.getBoundingClientRect();
      const nextScrollTop =
        drawerBody.scrollTop + (targetRect.top - drawerRect.top) - stickyOffset;

      drawerBody.scrollTo({
        top: Math.max(nextScrollTop, 0),
        behavior: "smooth",
      });
    });
  };

  const waitForStepRender = () =>
    new Promise<void>((resolve) => {
      setTimeout(() => resolve(), 0);
    });

  const backButtonLabelKey =
    currentStep === 2
      ? "COMMUNICATION.Button_BackToStep1"
      : "COMMUNICATION.Button_BackToStep2";

  const nextButtonLabelKey =
    currentStep === 1
      ? "COMMUNICATION.Button_ContinueToStep2"
      : "COMMUNICATION.Button_ContinueToStep3";

  const getStepTwoValidationFieldNames = (
    equipmentChanges: TCommunicationTaskFormValues["equipment_changes"]
  ): TValidationFieldName[] =>
    equipmentChanges.flatMap((change, index, changes) => {
      if (shouldIgnoreEquipmentChangeValidation(change, index, changes, hasExistingEquipmentChanges)) {
        return [];
      }

      return [
        [ "equipment_changes", index, "equipment_group_id" ],
        [ "equipment_changes", index, "qty" ],
      ];
    });

  const getQuestionValidationFieldNames = (): TValidationFieldName[] =>
    resolvedQuestions.map((_, index) => [ "questions", index, "text" ]);

  const getStepOneValidationFieldNames = (): TValidationFieldName[] => {
    const contextGroups = resolvedContextGroups.length > 0 ? resolvedContextGroups : [ {
      id: `context-${task.id}-0`,
      event_name: "",
      company_name: "",
      stand_number: "",
    } ];

    return contextGroups.flatMap((_, index) => ([
      [ "context_groups", index, "event_name" ],
      [ "context_groups", index, "company_name" ],
      [ "context_groups", index, "stand_number" ],
    ]));
  };

  const validateStepFields = async (
    stepId: number,
    fieldNames: TValidationFieldName[]
  ): Promise<boolean> => {
    if (!fieldNames.length) {
      return true;
    }

    if (currentStep !== stepId) {
      setCurrentStep(stepId);
      await waitForStepRender();
    }

    try {
      await form.validateFields(fieldNames);
      return true;
    } catch (error) {
      scrollToFirstFieldError(error as TValidateFieldsError);
      return false;
    }
  };

  const validateMatchingStep = async (): Promise<boolean> =>
    validateStepFields(1, getStepOneValidationFieldNames());

  const handleNextStep = async () => {
    if (currentStep === 1) {
      const isStepOneValid = await validateMatchingStep();

      if (!isStepOneValid) {
        return;
      }

      setMaxUnlockedStep((step) => (step < 2 ? 2 : step));
      setCurrentStep(2);
      return;
    }

    if (currentStep === 2) {
      if (!isMatchComplete) {
        await validateMatchingStep();
        return;
      }

      const isStepTwoValid = await validateStepFields(
        2,
        [
          ...getStepTwoValidationFieldNames(resolvedEquipmentChanges),
          ...getQuestionValidationFieldNames(),
        ]
      );

      if (!isStepTwoValid) {
        return;
      }

      setMaxUnlockedStep((step) => (step < 3 ? 3 : step));
      setCurrentStep(3);
      return;
    }
  };

  const handleApproveClick = async () => {
    if (!isMatchComplete) {
      await validateMatchingStep();
      return;
    }

    if (hasStepTwoBlockingIssues) {
      await validateStepFields(2, getStepTwoValidationFieldNames(resolvedEquipmentChanges));
      return;
    }

    void form.validateFields().then((validatedValues) => {
      const rawValues = form.getFieldsValue(true) as Partial<TCommunicationTaskFormValues>;
      const nextValues = buildFormValueSnapshot(task, {
        ...rawValues,
        ...validatedValues,
      }, {
        event_name: resolvedEventName,
        company_name: resolvedCompanyName,
        stand_number: primaryStandNumber,
        stand_numbers: normalizedStandNumbers,
        context_groups: resolvedContextGroups,
        equipment_changes: resolvedEquipmentChanges,
        stand_updates: resolvedStandUpdates,
        questions: resolvedQuestions,
      });

      onApprove(task.id, nextValues);
      hasTriggeredDirtyStartRef.current = false;
      setIsDirty(false);
      onDirtyChange?.(false);
      successNotification({
        title: localizeByKeyStatic("COMMUNICATION.Button_ApproveAndComplete"),
        description: localizeByKeyStatic("COMMUNICATION.Message_Approved"),
      });
      action?.({ ignoreUnsavedChanges: true });
    }).catch((error: TValidateFieldsError) => {
      scrollToFirstFieldError(error);
    });
  };

  const handleDismissNotRelevant = () => {
    if (!onDismissNotRelevant) {
      return;
    }

    onDismissNotRelevant(task.id, dismissReason);
    hasTriggeredDirtyStartRef.current = false;
    setIsDirty(false);
    onDirtyChange?.(false);
    setIsDismissModalOpen(false);
    action?.({ ignoreUnsavedChanges: true });
  };

  const patchEquipmentChange = (
    index: number,
    patch: Partial<TCommunicationTaskFormValues["equipment_changes"][number]>
  ) => {
    const equipmentChanges = form.getFieldValue("equipment_changes") || [];
    const nextEquipmentChanges = equipmentChanges.map(
      (
        change: TCommunicationTaskFormValues["equipment_changes"][number],
        changeIndex: number
      ) => (changeIndex === index ? { ...change, ...patch } : change)
    );

    form.setFieldValue("equipment_changes", nextEquipmentChanges);
  };

  const handleQuestionStatusToggle = (index: number, checked: boolean) => {
    form.setFieldValue(
      [ "questions", index, "status" ],
      checked ? "reviewed" : "open"
    );
  };

  const syncEquipmentChangeLabel = (index: number, equipmentGroupId?: string) => {
    const nextLabel = equipmentGroupId ? equipmentOptionsMap.get(equipmentGroupId) || "" : "";
    patchEquipmentChange(index, { item_label: nextLabel });
  };

  const handleConfirmEquipmentSuggestion = (index: number) => {
    const currentChange = form.getFieldValue([ "equipment_changes", index ]) || {};
    const nextEquipmentGroupId =
      currentChange.equipment_group_id || currentChange.suggested_equipment_group_id;

    patchEquipmentChange(index, {
      equipment_group_id: nextEquipmentGroupId,
      suggestion_status: "confirmed",
    });
    syncEquipmentChangeLabel(index, nextEquipmentGroupId);
  };

  const handleChooseAnotherEquipment = (index: number) => {
    patchEquipmentChange(index, {
      equipment_group_id: undefined,
      item_label: "",
      suggestion_status: "changed",
    });
    setOpenEquipmentField(index);
  };

  const handleEquipmentSelectChange = (index: number, equipmentGroupId?: string) => {
    const currentChange = form.getFieldValue([ "equipment_changes", index ]) || {};
    const suggestionMeta = getEquipmentSuggestionMeta(currentChange);

    patchEquipmentChange(index, {
      equipment_group_id: equipmentGroupId,
      suggestion_status:
        suggestionMeta
          ? equipmentGroupId && equipmentGroupId === suggestionMeta.suggestedGroupId
            ? "pending"
            : "changed"
          : currentChange.suggestion_status,
    });
    syncEquipmentChangeLabel(index, equipmentGroupId);

    if (equipmentGroupId) {
      setOpenEquipmentField(null);
    }
  };

  const handleEquipmentStandSelectChange = (index: number, value?: string) => {
    const nextStandNumber = typeof value === "string" ? value : "";
    const matchedContextGroup = resolvedContextGroups.find((group) =>
      group.stand_number.trim() === nextStandNumber.trim()
    );

    patchEquipmentChange(index, {
      stand_number: nextStandNumber,
      event_name: matchedContextGroup?.event_name || undefined,
      company_name: matchedContextGroup?.company_name || undefined,
    });
  };

  const validateEquipmentSelection = (index: number, value?: string) => {
    const equipmentChanges =
      (form.getFieldValue("equipment_changes") || []) as TCommunicationTaskFormValues["equipment_changes"];
    const currentEquipmentChange = equipmentChanges[index];

    if (
      shouldIgnoreEquipmentChangeValidation(
        currentEquipmentChange,
        index,
        equipmentChanges,
        hasExistingEquipmentChanges
      )
    ) {
      return Promise.resolve();
    }

    if ((typeof value === "string" && value.trim()) || getEffectiveEquipmentGroupId(currentEquipmentChange)) {
      return Promise.resolve();
    }

    return Promise.reject(
      new Error(getEquipmentValidationMessage(currentEquipmentChange))
    );
  };

  const handleEquipmentDropdownVisibleChange = (index: number, isOpen: boolean) => {
    setOpenEquipmentField(isOpen ? index : null);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      className={`model-form communication-task-form communication-task-form--${presentationMode}`}
      initialValues={initialFormValues}
      onValuesChange={() => {
        const rawValues = form.getFieldsValue(true) as Partial<TCommunicationTaskFormValues>;
        const nextSnapshot = JSON.stringify(
          normalizeTaskFormSnapshot(task, rawValues, equipmentIdToGroupIdMap)
        );
        const nextIsDirty = nextSnapshot !== initialFormSnapshot;

        if (nextIsDirty !== isDirty) {
          setIsDirty(nextIsDirty);
          onDirtyChange?.(nextIsDirty);
        }

        if (nextIsDirty && !hasTriggeredDirtyStartRef.current) {
          hasTriggeredDirtyStartRef.current = true;
          onDirtyStart?.(task.id);
        }
      }}
    >
      <div className="communication-task-form__sticky-shell">
        <div className="communication-task-form__context-ribbon">
          <div className="communication-task-form__context-ribbon-primary">
            <div className="communication-task-form__context-card communication-task-form__context-card--subject">
              <span className="communication-task-form__context-card-label">
                <Localize>FORMS.Input_Label_Subject</Localize>
              </span>
              <strong className="communication-task-form__context-card-title" title={task.subject}>
                {task.subject}
              </strong>
              <span className="communication-task-form__context-card-meta" title={resolvedEventName || task.event_name}>
                {resolvedEventName || task.event_name || "\u2014"}
              </span>
            </div>

            <div className="communication-task-form__context-card">
              <span className="communication-task-form__context-card-label">
                <Localize>FORMS.Input_Label_Sender</Localize>
              </span>
              <strong className="communication-task-form__context-card-title" title={task.sender}>
                {senderDisplayLabel}
              </strong>
              <span className="communication-task-form__context-card-meta" title={resolvedCompanyName || task.company_name}>
                {resolvedCompanyName || task.company_name || "\u2014"}
              </span>
            </div>

            <div
              className={`communication-task-form__context-card communication-task-form__context-card--confidence communication-task-form__context-card--confidence-${confidenceTone}`}
            >
              <span className="communication-task-form__context-card-label">
                <Localize>FORMS.Input_Label_Confidence</Localize>
              </span>
              <div className="communication-task-form__confidence-value">
                <strong className="communication-task-form__confidence-percent">{`${confidenceValue}%`}</strong>
                <span className={`communication-task-form__confidence-chip communication-task-form__confidence-chip--${confidenceTone}`}>
                  <Localize>
                    {confidenceTone === "high"
                      ? "COMMUNICATION.Confidence_High"
                      : confidenceTone === "medium"
                        ? "COMMUNICATION.Confidence_Medium"
                        : "COMMUNICATION.Confidence_Low"}
                  </Localize>
                </span>
              </div>
              <span className="communication-task-form__confidence-track" aria-hidden={true}>
                <span
                  className={`communication-task-form__confidence-fill communication-task-form__confidence-fill--${confidenceTone}`}
                  style={{ width: `${confidenceValue}%` }}
                />
              </span>
            </div>
          </div>

          {shouldRenderSecondaryContextRibbon && (
            <div className="communication-task-form__context-ribbon-secondary">
              <span className="communication-task-form__context-chip communication-task-form__context-chip--state">
                <span className="communication-task-form__context-chip-label">
                  <Localize>COMMUNICATION.WorkState_Label</Localize>
                </span>
                <strong
                  className={`communication-task-form__work-state-badge communication-task-form__work-state-badge--${task.work_state.status}`}
                >
                  {workStateLabel}
                </strong>
              </span>

              <span className="communication-task-form__context-chip">
                <span className="communication-task-form__context-chip-label">
                  <Localize>FORMS.Input_Label_ReceivedAt</Localize>
                </span>
                <strong>{formatCommunicationDateTime(task.received_at)}</strong>
              </span>

              <span className="communication-task-form__context-chip">
                <span className="communication-task-form__context-chip-label">
                  <Localize>{contextStandLabels.length > 1 ? "COMMUNICATION.Field_StandNumbers" : "FORMS.Input_Label_StandNumber"}</Localize>
                </span>
                <strong title={standsSummaryLabel}>{standsSummaryLabel}</strong>
              </span>

              {aiSourceLabelKey && (
                <span className="communication-task-form__context-chip communication-task-form__context-chip--ai">
                  <span className="communication-task-form__context-chip-label">
                    <Localize>COMMUNICATION.Source_Label</Localize>
                  </span>
                  <strong>
                    <Localize>{aiSourceLabelKey}</Localize>
                  </strong>
                </span>
              )}

              {task.ai_selected_response_id && aiParserLabel && (
                <span className="communication-task-form__context-chip communication-task-form__context-chip--ai">
                  <span className="communication-task-form__context-chip-label">
                    <Localize>COMMUNICATION.AiParser_Label</Localize>
                  </span>
                  <strong title={aiParserLabel}>{aiParserLabel}</strong>
                </span>
              )}

              {typeof task.ai_response_count_total === "number" && task.ai_response_count_total > 1 && (
                <span className="communication-task-form__context-chip communication-task-form__context-chip--muted">
                  <span className="communication-task-form__context-chip-label">
                    <Localize>COMMUNICATION.AiAttempts_Label</Localize>
                  </span>
                  <strong>
                    {localizeByKeyStatic("COMMUNICATION.AiAttempts_Value", {
                      count: task.ai_response_count_total,
                    })}
                  </strong>
                </span>
              )}

              {typeof task.ai_response_count_processing === "number" && task.ai_response_count_processing > 0 && (
                <span className="communication-task-form__context-chip communication-task-form__context-chip--muted">
                  <span className="communication-task-form__context-chip-label">
                    <Localize>COMMUNICATION.AiProcessing_Label</Localize>
                  </span>
                  <strong>
                    {localizeByKeyStatic("COMMUNICATION.AiProcessing_Value", {
                      count: task.ai_response_count_processing,
                    })}
                  </strong>
                </span>
              )}

              {task.ai_attempt && task.ai_attempt > 1 && (
                <span className="communication-task-form__context-chip communication-task-form__context-chip--muted">
                  <span className="communication-task-form__context-chip-label">
                    <Localize>COMMUNICATION.AiParser_Selected</Localize>
                  </span>
                  <strong>
                    {localizeByKeyStatic("COMMUNICATION.AiAttempt_Value", {
                      count: task.ai_attempt,
                    })}
                  </strong>
                </span>
              )}

              {task.ai_needs_review && (
                <span className="communication-task-form__context-chip communication-task-form__context-chip--warning">
                  <span className="communication-task-form__context-chip-label">
                    <Localize>COMMUNICATION.AiReview_Label</Localize>
                  </span>
                  <strong>
                    <Localize>COMMUNICATION.AiReview_Required</Localize>
                  </strong>
                </span>
              )}

              {totalClarificationCount > 0 && (
                <span className="communication-task-form__context-chip communication-task-form__context-chip--warning">
                  <span className="communication-task-form__context-chip-label">
                    <Localize>COMMUNICATION.Step2Summary_ClarificationsLabel</Localize>
                  </span>
                  <strong>
                    {localizeByKeyStatic("COMMUNICATION.Text_QuestionsSummary", {
                      open: openQuestionsCount,
                      questions: totalClarificationCount,
                    })}
                  </strong>
                </span>
              )}

              {(!isMatchComplete || unresolvedReviewCount > 0) && (
                <span className="communication-task-form__context-chip communication-task-form__context-chip--warning">
                  <span className="communication-task-form__context-chip-label">
                    <Localize>
                      {isMatchComplete
                        ? "COMMUNICATION.Step2Summary_ChangesLabel"
                        : "COMMUNICATION.Info_NeedsMatch"}
                    </Localize>
                  </span>
                  <strong>
                    {matchingMissingFieldLabels.length > 0
                      ? matchingMissingFieldLabels.join(", ")
                      : localizeByKeyStatic(
                        isMatchComplete
                          ? "COMMUNICATION.StepStatus_ReviewCount"
                          : "COMMUNICATION.Step1Summary_UnresolvedHint",
                        {
                          count: unresolvedReviewCount,
                        }
                      )}
                  </strong>
                </span>
              )}

              {hasSpamSignal && (
                <span className="communication-task-form__context-chip communication-task-form__context-chip--spam">
                  <span className="communication-task-form__context-chip-label">
                    <Localize>COMMUNICATION.Info_SpamSignal</Localize>
                  </span>
                  <strong>
                    <Localize>COMMUNICATION.NotRelevantReason_spam_marketing</Localize>
                  </strong>
                </span>
              )}

              {isDismissedHistoryEntry && (
                <span className="communication-task-form__context-chip communication-task-form__context-chip--dismissed">
                  <span className="communication-task-form__context-chip-label">
                    <Localize>COMMUNICATION.Audit_Outcome</Localize>
                  </span>
                  <strong>
                    <Localize>COMMUNICATION.AuditOutcome_Dismissed</Localize>
                  </strong>
                </span>
              )}

              {isDismissedHistoryEntry && dismissedReasonLabel && (
                <span className="communication-task-form__context-chip communication-task-form__context-chip--dismissed">
                  <span className="communication-task-form__context-chip-label">
                    <Localize>COMMUNICATION.Audit_Reason</Localize>
                  </span>
                  <strong>{dismissedReasonLabel}</strong>
                </span>
              )}

              {workStateTouchedLabel && (
                <span className="communication-task-form__context-chip communication-task-form__context-chip--muted">
                  <strong>{workStateTouchedLabel}</strong>
                </span>
              )}
            </div>
          )}
        </div>

        {shouldRenderDemoStrip && task.demo_badge_key && task.demo_note_key && (
          <div className="communication-task-form__demo-strip">
            <span className="communication-task-form__demo-strip-icon" aria-hidden={true}>
              <Icons.Information />
            </span>
            <div className="communication-task-form__demo-strip-copy">
              <strong><Localize>{task.demo_badge_key}</Localize></strong>
              <span><Localize>{task.demo_note_key}</Localize></span>
            </div>
          </div>
        )}

        <div className={`communication-task-form__reading-pane${isReadingPaneExpanded ? " is-expanded" : " is-collapsed"}`}>
          <div className="communication-task-form__reading-pane-header">
            <div className="communication-task-form__reading-pane-title">
              <span className="communication-task-form__reading-pane-icon" aria-hidden={true}>
                <Icons.Document />
              </span>
              <div className="communication-task-form__reading-pane-copy">
                <span className="communication-task-form__reading-pane-label">
                  <Localize>FORMS.Input_Label_OriginalEmail</Localize>
                </span>
                <strong>
                  <Localize>COMMUNICATION.Section_AIContextSecondary</Localize>
                </strong>
              </div>
            </div>

            <Button
              className="light-bg communication-task-form__reading-pane-toggle"
              icon={<Icons.Arrow />}
              onClick={() => setIsReadingPaneExpanded((current) => !current)}
            >
              <span className="ellipsis">
                <Localize>
                  {isReadingPaneExpanded
                    ? "COMMUNICATION.Button_CollapseEmail"
                    : "COMMUNICATION.Button_ExpandEmail"}
                </Localize>
              </span>
            </Button>
          </div>

          {isReadingPaneExpanded ? (
            <div className="communication-task-form__reading-pane-body">
                <CommunicationOriginalEmailField
                  originalBody={task.original_body}
                  equipmentChanges={originalEmailEquipmentChanges}
                  headerMeta={task.original_email_meta}
                  isExpanded={true}
                  maxHeightReductionPx={shouldAutoCondenseReadingPane && isReadingPaneScrollCondensed ? 200 : 0}
                />
            </div>
          ) : (
            <button
              type="button"
              className="communication-task-form__reading-pane-summary"
              onClick={() => setIsReadingPaneExpanded(true)}
            >
              <span className="communication-task-form__reading-pane-summary-text">
                {originalEmailSummary}
              </span>
            </button>
          )}
        </div>

        {!isDismissedHistoryEntry && (
          <div className="communication-task-form__steps-sticky">
            <div className="communication-task-form__steps">
              {steps.map((step) => (
                <button
                  key={step.id}
                  type="button"
                  className={`communication-task-form__step communication-task-form__step--${step.status} ${currentStep === step.id ? "is-current" : ""}`}
                  onClick={() => setCurrentStep(step.id)}
                  disabled={!step.isClickable}
                  aria-current={currentStep === step.id ? "step" : undefined}
                  aria-label={`${getStepTitle(step)}. ${getStepBadgeLabel(step)}`}
                >
                  <span className="communication-task-form__step-number">{step.id}</span>
                  <span
                    className={`communication-task-form__step-icon communication-task-form__step-icon--${step.status}`}
                    aria-hidden={true}
                  >
                    {renderStepIcon(step)}
                  </span>
                  <span className="communication-task-form__step-copy">
                  <span className="communication-task-form__step-head">
                    <span className="communication-task-form__step-text">{getStepTitle(step)}</span>
                    <span className={`communication-task-form__step-badge communication-task-form__step-badge--${step.status}`}>
                      {getStepBadgeLabel(step)}
                    </span>
                  </span>
                  {shouldShowStepDescriptions && (
                    <span className="communication-task-form__step-description">{getStepDescription(step)}</span>
                  )}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {isDismissedHistoryEntry && (
        <div className="communication-task-form__section">
          <div className="communication-task-form__preview-card communication-task-form__preview-card--full communication-task-form__preview-card--secondary communication-task-form__preview-card--dismissed">
            <div className="communication-task-form__preview-header">
              <span className="communication-task-form__preview-title">
                <Localize>COMMUNICATION.NotRelevantSummary_Title</Localize>
              </span>
              <span className="communication-task-form__preview-count communication-task-form__preview-count--warning">
                <Localize>COMMUNICATION.Info_NotRelevant</Localize>
              </span>
            </div>
            <small className="communication-task-form__preview-hint">
              <Localize>COMMUNICATION.NotRelevantSummary_Hint</Localize>
            </small>

            <div className="communication-task-form__dismissed-summary-meta">
              <span className="communication-task-form__question-meta-chip communication-task-form__question-meta-chip--warning">
                <Localize>COMMUNICATION.AuditOutcome_Dismissed</Localize>
              </span>
              {dismissedReasonLabel && (
                <span className="communication-task-form__question-meta-chip">
                  {`${localizeByKeyStatic("COMMUNICATION.Audit_Reason")}: ${dismissedReasonLabel}`}
                </span>
              )}
            </div>

            <div className="communication-task-form__dismissed-summary-grid">
              <div className="communication-task-form__preview-line">
                <strong><Localize>COMMUNICATION.Audit_Outcome</Localize>:</strong>{" "}
                <Localize>COMMUNICATION.AuditOutcome_Dismissed</Localize>
              </div>
              <div className="communication-task-form__preview-line communication-task-form__preview-line--multiline">
                <strong><Localize>FORMS.Input_Label_AISummary</Localize>:</strong>{" "}
                {dismissedEntrySummary}
              </div>
            </div>
          </div>
        </div>
      )}

      {currentStep === 3 && !isDismissedHistoryEntry && (
        <div className="communication-task-form__preview-card communication-task-form__preview-card--full communication-task-form__preview-card--final-primary">
          <div className="communication-task-form__preview-header">
            <div className="communication-task-form__preview-header-copy">
              <span className="communication-task-form__preview-title">
                <Localize>COMMUNICATION.Preview_LogisticsFinal</Localize>
              </span>
              {excludedContextGroups.length > 0 && (
                <span className="communication-task-form__preview-count communication-task-form__preview-count--warning">
                  {excludedContextGroups.length === 1 ? "1 stand excluded by manager" : `${excludedContextGroups.length} stands excluded by manager`}
                </span>
              )}
            </div>
            <span className="communication-task-form__preview-count">
              <Localize count={finalLogisticsPreview.length}>COMMUNICATION.Text_ChangesCount</Localize>
            </span>
          </div>
          <small className="communication-task-form__preview-hint">
            <Localize>COMMUNICATION.Preview_LogisticsFinalHint</Localize>
          </small>

          <CommunicationTaskLogisticsTable
            rows={finalLogisticsPreview}
            emptyKey="COMMUNICATION.Text_NoFinalChanges"
            productLabelKey="COMMUNICATION.Preview_Product"
            shouldColorizeStandGroups={shouldColorizeStandGroups}
            excludedContextGroups={excludedContextGroups}
            formatCanonicalStandNumberLabel={formatCanonicalStandNumberLabel}
            formatStandGroupTitle={formatStandGroupTitle}
            getCountLabel={getCountLabel}
            getStandToneProps={getStandToneProps}
            actionLabelKeyMap={actionLabelKeyMap}
          />
        </div>
      )}

      {currentStep === 1 && !isDismissedHistoryEntry && (
        <div className="communication-task-form__section">
          <Form.Item name="event_name" hidden={true}>
            <Input />
          </Form.Item>
          <Form.Item name="company_name" hidden={true}>
            <Input />
          </Form.Item>
          <Form.Item name="stand_number" hidden={true}>
            <Input />
          </Form.Item>

          {shouldRenderStepOneSummary && (
            <div className="communication-task-form__review-summary communication-task-form__review-summary--strip communication-task-form__review-summary--strip-four">
              {stepOneSummaryItems.map((item) => (
                <div
                  key={item.key}
                  className={`communication-task-form__review-summary-item communication-task-form__review-summary-item--${item.tone}`}
                >
                  <span className="communication-task-form__review-summary-label">
                    <Localize>{item.labelKey}</Localize>
                  </span>
                  <strong className="communication-task-form__review-summary-value">
                    {item.value}
                  </strong>
                  <small className="communication-task-form__review-summary-caption">
                    {item.caption}
                  </small>
                </div>
              ))}
            </div>
          )}

          <div className={`communication-task-form__step-one-layout${requestGroupCount === 0 ? " communication-task-form__step-one-layout--single" : ""}`}>
            <div className="communication-task-form__step-one-main">
              <div className="communication-task-form__matching-panel">
                <div className="communication-task-form__stand-list communication-task-form__stand-list--contexts">
                  <div className="communication-task-form__stand-list-header">
                    <div className="communication-task-form__stand-list-copy">
                      <strong className="communication-task-form__stand-list-title">
                        <Localize>COMMUNICATION.Section_RequestGroups</Localize>
                      </strong>
                      <small className="communication-task-form__stand-list-hint">
                        Confirm event, company, and stand for each detected context group.
                      </small>
                    </div>

                    <Button
                      className="light-bg communication-task-form__stand-add"
                      icon={<Icons.Plus />}
                      onClick={() => {
                        const nextContextGroups = [
                          ...resolvedContextGroups,
                          {
                            id: `context-${task.id}-${Date.now()}`,
                            event_name: "",
                            company_name: "",
                            stand_number: "",
                          },
                        ];

                        form.setFieldValue("context_groups", nextContextGroups);
                      }}
                    >
                      Add group
                    </Button>
                  </div>

                  <Form.List name="context_groups">
                    {(fields) => (
                      <div className="communication-task-form__stand-list-rows communication-task-form__stand-list-rows--contexts">
                        {fields.map((field) => (
                          <div key={field.key} className="communication-task-form__context-row">
                            <div className="communication-task-form__context-row-fields">
                              <Form.Item
                                name={[field.name, "id"]}
                                hidden={true}
                              >
                                <Input />
                              </Form.Item>
                              <Form.Item
                                name={[field.name, "event_name"]}
                                label={<Localize>FORMS.Input_Label_EventName</Localize>}
                                rules={[{
                                  required: true,
                                  whitespace: true,
                                  message: localizeByKeyStatic("FORM_RULES.Required_EventName"),
                                }]}
                              >
                                <Input />
                              </Form.Item>
                              <Form.Item
                                name={[field.name, "company_name"]}
                                label={<Localize>FORMS.Input_Label_CompanyName</Localize>}
                                rules={[{
                                  required: true,
                                  whitespace: true,
                                  message: localizeByKeyStatic("FORM_RULES.Required_CompanyName"),
                                }]}
                              >
                                <Input />
                              </Form.Item>
                              <Form.Item
                                name={[field.name, "stand_number"]}
                                label={<Localize>FORMS.Input_Label_StandNumber</Localize>}
                                rules={[{
                                  required: true,
                                  whitespace: true,
                                  message: localizeByKeyStatic("FORM_RULES.Required_StandNumber"),
                                }]}
                              >
                                <Input placeholder={localizeByKeyStatic("FORMS.Input_Label_StandNumber")} />
                              </Form.Item>
                            </div>
                            <div className="communication-task-form__context-row-actions">
                              <Button
                                className="communication-task-form__stand-remove communication-task-form__stand-remove--context"
                                disabled={fields.length <= 1}
                                onClick={() => {
                                  const nextContextGroups = resolvedContextGroups.filter((_, index) => index !== field.name);
                                  form.setFieldValue("context_groups", nextContextGroups);
                                }}
                              >
                                <span className="communication-task-form__stand-remove-icon">
                                  <Icons.Delete />
                                </span>
                                <span className="communication-task-form__stand-remove-label">
                                  <Localize>GLOBAL.Button_Text_Remove</Localize>
                                </span>
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Form.List>
                </div>

                <div className="row-grid grid-gap-10 row-gap-0 col-md-2 communication-task-form__matching-grid">
                  <Form.Item name="organizer_id" label={<Localize>FORMS.Input_Label_Organizer</Localize>}>
                    <Select
                      allowClear
                      options={organizerOptions}
                      suffixIcon={<Icons.Arrow />}
                      placeholder={localizeByKeyStatic("FORMS.Input_Placeholder_Choose")}
                    />
                  </Form.Item>
                  <Form.Item
                    name="exhibition_type_id"
                    label={<Localize>FORMS.Input_Label_ExhibitionTypes</Localize>}
                  >
                    <Select
                      allowClear
                      options={exhibitionOptions}
                      suffixIcon={<Icons.Arrow />}
                      placeholder={localizeByKeyStatic("FORMS.Input_Placeholder_Choose")}
                    />
                  </Form.Item>
                </div>
              </div>

              {shouldRenderStepOneFollowUpStrip && (
                <div className="communication-task-form__followup-strip">
                  <div className="communication-task-form__followup-strip-copy">
                    <span className="communication-task-form__questions-eyebrow">
                      <Localize>COMMUNICATION.QuestionsWorkflow_Label</Localize>
                    </span>
                    <strong className="communication-task-form__followup-strip-title">
                      <Localize>COMMUNICATION.QuestionsWorkflow_Title</Localize>
                    </strong>
                    <span className="communication-task-form__followup-strip-hint">
                      <Localize>COMMUNICATION.QuestionsWorkflow_Step2Hint</Localize>
                    </span>
                  </div>

                  <div className="communication-task-form__followup-strip-meta">
                    {followUpStandNumber && (
                      <span className="communication-task-form__question-meta-chip">
                        {localizeByKeyStatic("COMMUNICATION.QuestionsWorkflow_RelatedStand", {
                          stand: formatCanonicalStandNumberLabel(followUpStandNumber),
                        })}
                      </span>
                    )}
                    <span
                      className={`communication-task-form__question-meta-chip${totalClarificationCount > 0 ? " communication-task-form__question-meta-chip--warning" : " communication-task-form__question-meta-chip--muted"}`}
                    >
                      {totalClarificationCount > 0
                        ? localizeByKeyStatic("COMMUNICATION.Text_QuestionsSummary", {
                          open: openQuestionsCount,
                          questions: totalClarificationCount,
                        })
                        : localizeByKeyStatic("COMMUNICATION.Text_NoQuestions")}
                    </span>
                    <span
                      className={`communication-task-form__question-meta-chip${unresolvedReviewCount > 0 ? " communication-task-form__question-meta-chip--warning" : " communication-task-form__question-meta-chip--muted"}`}
                    >
                      {unresolvedReviewCount > 0
                        ? localizeByKeyStatic("COMMUNICATION.Step1Summary_UnresolvedHint", {
                          count: unresolvedReviewCount,
                        })
                        : localizeByKeyStatic("COMMUNICATION.Step1Summary_UnresolvedClear")}
                    </span>
                    {(totalClarificationCount > 0 || unresolvedReviewCount > 0) && (
                      <span className="communication-task-form__question-meta-chip communication-task-form__question-meta-chip--warning">
                        <Localize>COMMUNICATION.QuestionsWorkflow_AffectsChanges</Localize>
                      </span>
                    )}
                    <span className="communication-task-form__question-meta-chip communication-task-form__question-meta-chip--muted">
                      <Localize>COMMUNICATION.QuestionsWorkflow_Step2Continue</Localize>
                    </span>
                  </div>
                </div>
              )}
            </div>

            {requestGroupCount > 0 && (
              <div className="communication-task-form__step-one-side">
                <div className="communication-task-form__preview-card communication-task-form__preview-card--full communication-task-form__preview-card--context communication-task-form__preview-card--source">
                  <div className="communication-task-form__preview-header">
                    <span className="communication-task-form__preview-title">
                      <Localize>COMMUNICATION.Section_RequestGroups</Localize>
                    </span>
                    <span className="communication-task-form__preview-count">
                      {`${getCountLabel(
                        requestGroupCount,
                        "COMMUNICATION.RequestGroup_GroupSingle",
                        "COMMUNICATION.RequestGroup_Groups"
                      )} / ${getCountLabel(
                        visibleRequestItemsCount,
                        "COMMUNICATION.RequestGroup_ParsedItemSingle",
                        "COMMUNICATION.RequestGroup_ParsedItems"
                      )}`}
                    </span>
                  </div>
                  <small className="communication-task-form__preview-hint">
                    <Localize>{requestGroupsHintKey}</Localize>
                  </small>

                  {hasMultipleRequestGroups && (
                    <Alert
                      className="communication-task-form__preview-note"
                      type="info"
                      showIcon
                      message={<Localize>COMMUNICATION.Concept_MultiGroupHint</Localize>}
                    />
                  )}

                  <CommunicationTaskRequestGroups
                    groups={displayRequestGroups}
                    shouldColorizeStandGroups={shouldColorizeStandGroups}
                    formatStandGroupTitle={formatStandGroupTitle}
                    getCountLabel={getCountLabel}
                    getRequestStatusTone={getRequestStatusTone}
                    getStandToneProps={getStandToneProps}
                    requestActionLabelKeyMap={requestActionLabelKeyMap}
                    requestStatusLabelKeyMap={requestStatusLabelKeyMap}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {currentStep === 2 && !isDismissedHistoryEntry && (
        <div className="communication-task-form__section communication-task-form__section--workflow">
          <div className="communication-task-form__workflow-canvas communication-task-form__workflow-canvas--step-two">
          {shouldRenderStepTwoSummary && (
            <div className="communication-task-form__review-summary communication-task-form__review-summary--strip">
              {stepTwoSummaryItems.map((item) => (
                <div
                  key={item.key}
                  className={`communication-task-form__review-summary-item communication-task-form__review-summary-item--${item.tone}`}
                >
                  <span className="communication-task-form__review-summary-label">
                    <Localize>{item.labelKey}</Localize>
                  </span>
                  <strong className="communication-task-form__review-summary-value">
                    {item.value}
                  </strong>
                  <small className="communication-task-form__review-summary-caption">
                    {item.caption}
                  </small>
                </div>
              ))}
            </div>
          )}

          {(task.ai_needs_review || pendingEquipmentSuggestionsCount > 0 || unresolvedEquipmentSelectionCount > 0) && (
            <div className="communication-task-form__context-ribbon-secondary">
              {task.ai_needs_review && (
                <span className="communication-task-form__context-chip communication-task-form__context-chip--warning">
                  <span className="communication-task-form__context-chip-label">
                    <Localize>COMMUNICATION.AiReview_Label</Localize>
                  </span>
                  <strong>
                    <Localize>COMMUNICATION.AiReview_Required</Localize>
                  </strong>
                </span>
              )}

              {pendingEquipmentSuggestionsCount > 0 && (
                <span className="communication-task-form__context-chip communication-task-form__context-chip--ai">
                  <span className="communication-task-form__context-chip-label">
                    <Localize>COMMUNICATION.EquipmentSuggestion_Badge</Localize>
                  </span>
                  <strong>
                    {localizeByKeyStatic("COMMUNICATION.AiSuggestion_Value", {
                      count: pendingEquipmentSuggestionsCount,
                    })}
                  </strong>
                </span>
              )}

              {unresolvedEquipmentSelectionCount > 0 && (
                <span className="communication-task-form__context-chip communication-task-form__context-chip--warning">
                  <span className="communication-task-form__context-chip-label">
                    <Localize>COMMUNICATION.Step2Summary_ChangesLabel</Localize>
                  </span>
                  <strong>
                    {localizeByKeyStatic("COMMUNICATION.StepStatus_ReviewCount", {
                      count: unresolvedEquipmentSelectionCount,
                    })}
                  </strong>
                </span>
              )}
            </div>
          )}

          {shouldRenderExtractedLogisticsBand && (
            <div className="communication-task-form__preview-card communication-task-form__preview-card--full communication-task-form__preview-card--source communication-task-form__preview-card--band communication-task-form__preview-card--source-band">
              <div className="communication-task-form__preview-header">
                <span className="communication-task-form__preview-title">
                  <Localize>COMMUNICATION.Preview_LogisticsExtracted</Localize>
                </span>
                <span className="communication-task-form__preview-count">
                  <Localize count={extractedLogisticsPreview.length}>COMMUNICATION.Text_ChangesCount</Localize>
                </span>
              </div>
              <small className="communication-task-form__preview-hint">
                <Localize>{extractedChangesHintKey}</Localize>
              </small>

              <CommunicationTaskLogisticsTable
                rows={extractedLogisticsPreview}
                emptyKey="COMMUNICATION.Text_NoExtractedChanges"
                productLabelKey="COMMUNICATION.Preview_ItemFromEmail"
                shouldColorizeStandGroups={shouldColorizeStandGroups}
                formatCanonicalStandNumberLabel={formatCanonicalStandNumberLabel}
                formatStandGroupTitle={formatStandGroupTitle}
                getCountLabel={getCountLabel}
                getStandToneProps={getStandToneProps}
                actionLabelKeyMap={actionLabelKeyMap}
              />
            </div>
          )}

          <div className="communication-task-form__workflow-main">
            {!hasMultiStandEditing && (
              <div className="communication-task-form__workflow-tools">
                <Button
                  className="light-bg communication-task-form__workflow-tool-btn"
                  icon={<Icons.Note />}
                  disabled
                >
                  <span className="ellipsis">
                    <Localize>COMMUNICATION.Button_ViewStandDrawing</Localize>
                  </span>
                </Button>
                <small className="communication-task-form__workflow-tool-note">
                  <Localize>COMMUNICATION.CadPreview_Hint</Localize>
                </small>
              </div>
            )}

            {pendingEquipmentSuggestionsCount > 0 && (
              <Alert
                className="communication-task-form__suggestions-alert"
                showIcon
                type="warning"
                message={
                  <Localize count={pendingEquipmentSuggestionsCount}>
                    COMMUNICATION.EquipmentSuggestion_PendingSummary
                  </Localize>
                }
              />
            )}

            <Form.List name="equipment_changes">
              {(fields, { add, remove }) => (
                <CommunicationTaskEquipmentChangesEditor
                  fields={fields}
                  hasMultiStandEditing={hasMultiStandEditing}
                  editableChangeGroups={editableChangeGroups}
                  primaryEventName={primaryContextGroup?.event_name || resolvedEventName || undefined}
                  primaryCompanyName={primaryContextGroup?.company_name || resolvedCompanyName || undefined}
                  primaryStandNumber={primaryStandNumber || undefined}
                  watchedEquipmentChanges={watchedEquipmentChanges}
                  actionOptions={actionOptions}
                  standSelectOptions={standSelectOptions}
                  equipmentSelectOptions={equipmentSelectOptions}
                  equipmentOptionsMap={equipmentOptionsMap}
                  openEquipmentField={openEquipmentField}
                  shouldColorizeStandGroups={shouldColorizeStandGroups}
                  formatStandGroupTitle={formatStandGroupTitle}
                  getCountLabel={getCountLabel}
                  getStandToneProps={getStandToneProps}
                  suggestionStatusLabelKeyMap={suggestionStatusLabelKeyMap}
                  validateEquipmentSelection={validateEquipmentSelection}
                  handleStandSelectChange={handleEquipmentStandSelectChange}
                  handleConfirmEquipmentSuggestion={handleConfirmEquipmentSuggestion}
                  handleChooseAnotherEquipment={handleChooseAnotherEquipment}
                  handleEquipmentSelectChange={handleEquipmentSelectChange}
                  handleEquipmentDropdownVisibleChange={handleEquipmentDropdownVisibleChange}
                  handleRemoveEquipmentChange={(index) => {
                    setOpenEquipmentField(null);
                    remove(index);
                  }}
                  handleAddEquipmentChange={() =>
                    add(
                      createEmptyEquipmentChange(
                        task.id,
                        hasMultiStandEditing ? undefined : primaryStandNumber
                      )
                    )
                  }
                />
              )}
            </Form.List>

            <CommunicationTaskClarificationsPanel
              resolvedQuestions={resolvedQuestions}
              openQuestionsCount={openQuestionsCount}
              unresolvedReviewCount={unresolvedReviewCount}
              followUpStandNumber={followUpStandNumber}
              clarificationStandContextLabel={clarificationStandContextLabel}
              formatCanonicalStandNumberLabel={formatCanonicalStandNumberLabel}
              questionStatusLabelMap={questionStatusLabelMap}
              questionStatusOptions={questionStatusOptions}
              handleQuestionStatusToggle={handleQuestionStatusToggle}
            />

            <CommunicationTaskReplyDraftPanel
              showPanel={shouldShowReplyHandoffNote || hasReplyHandoffNote}
              hasReplyHandoffNote={hasReplyHandoffNote}
              followUpStandNumber={followUpStandNumber}
              formatCanonicalStandNumberLabel={formatCanonicalStandNumberLabel}
              replyDraftRecipientLabel={replyDraftRecipientLabel}
              replyDraftSubject={replyDraftSubject || task.subject}
              replyDraftTemplates={replyDraftTemplates}
              canOpenReplyDraft={Boolean(replyDraftRecipient)}
              handleApplyReplyDraftTemplate={(templateKey) =>
                applyReplyDraftTemplate(templateKey as TReplyDraftTemplateKey)
              }
              handleOpenReplyDraft={handleOpenReplyDraft}
              handleShowReplyHandoffNote={() => setShowReplyHandoffNote(true)}
            />

            {showStandUpdates && (
              <div className="row-grid grid-gap-10 row-gap-0 col-md-2 communication-task-form__flooring">
              <Form.Item
                name={["stand_updates", "flooring_id"]}
                label={<Localize>FORMS.Input_Label_Flooring</Localize>}
              >
                <Select
                  suffixIcon={<Icons.Arrow />}
                  allowClear
                  options={flooringOptions}
                  placeholder={localizeByKeyStatic("FORMS.Input_Placeholder_Choose")}
                />
              </Form.Item>
              <Form.Item
                name={["stand_updates", "flooring_color"]}
                label={<Localize>FORMS.Input_Label_CarpetColor</Localize>}
              >
                <Input />
              </Form.Item>
            </div>
            )}

            {showManagerNote && (
              <Form.Item name="manager_note" label={<Localize>FORMS.Input_Label_ManagerNote</Localize>}>
                <TextArea rows={4} />
              </Form.Item>
            )}
          </div>
          </div>
        </div>
      )}

      {currentStep === 3 && !isDismissedHistoryEntry && (
        <div className="communication-task-form__section">
          <div className="communication-task-form__preview-card communication-task-form__preview-card--full communication-task-form__preview-card--secondary">
            <div className="communication-task-form__preview-header">
              <span className="communication-task-form__preview-title">
                <Localize>COMMUNICATION.Preview_QuestionsResult</Localize>
              </span>
              <span className={`communication-task-form__questions-badge${openQuestionsCount > 0 ? " communication-task-form__questions-badge--warning" : ""}`}>
                {resolvedQuestions.length > 0 ? (
                  <Localize
                    questions={resolvedQuestions.length}
                    open={openQuestionsCount}
                  >
                    COMMUNICATION.Text_QuestionsSummary
                  </Localize>
                ) : (
                  <Localize>COMMUNICATION.Text_NoQuestions</Localize>
                )}
              </span>
            </div>
            <small className="communication-task-form__preview-hint">
              <Localize
                reviewed={reviewedQuestionsCount}
                questions={resolvedQuestions.length}
              >
                COMMUNICATION.Preview_QuestionsResultHint
              </Localize>
            </small>

            {resolvedQuestions.length > 0 ? (
              <div className="communication-task-form__question-result-list">
                {resolvedQuestions.map((question, index) => {
                  const questionStatus = question?.status || "open";

                  return (
                    <div
                      key={question?.id || `question-result-${index}`}
                      className={`communication-task-form__question-result communication-task-form__question-result--${questionStatus}`}
                    >
                      <div className="communication-task-form__question-result-header">
                        <strong className="communication-task-form__question-result-title">
                          {`${localizeByKeyStatic("COMMUNICATION.Field_Clarification")} #${index + 1}`}
                        </strong>
                        <span
                          className={`communication-task-form__question-card-state communication-task-form__question-card-state--${questionStatus}`}
                        >
                          {questionStatusLabelMap[questionStatus]}
                        </span>
                      </div>
                      <div className="communication-task-form__question-result-text">
                        {question?.text?.trim() || localizeByKeyStatic("COMMUNICATION.Text_NoQuestions")}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="communication-task-form__questions-empty">
                <Localize>COMMUNICATION.Text_NoQuestions</Localize>
              </div>
            )}
          </div>

          <div className="communication-task-form__preview-card communication-task-form__preview-card--full communication-task-form__preview-card--secondary communication-task-form__preview-card--final-followup">
            <div className="communication-task-form__preview-header">
              <span className="communication-task-form__preview-title">
                <Localize>COMMUNICATION.FinalFollowUp_Title</Localize>
              </span>
              <span className={`communication-task-form__preview-count communication-task-form__preview-count--${needsCustomerFollowUp ? "warning" : "success"}`}>
                <Localize>{nextActionLabelKey}</Localize>
              </span>
            </div>
            <small className="communication-task-form__preview-hint">
              <Localize>COMMUNICATION.FinalFollowUp_Hint</Localize>
            </small>

            <div className="communication-task-form__reply-draft-addresses communication-task-form__reply-draft-addresses--summary">
              <div className="communication-task-form__reply-draft-address">
                <span className="communication-task-form__reply-draft-address-label">
                  <Localize>COMMUNICATION.ReplyDraft_To</Localize>
                </span>
                <strong
                  className="communication-task-form__reply-draft-address-value"
                  title={replyDraftRecipientLabel}
                >
                  {replyDraftRecipientLabel}
                </strong>
              </div>
              <div className="communication-task-form__reply-draft-address">
                <span className="communication-task-form__reply-draft-address-label">
                  <Localize>COMMUNICATION.ReplyDraft_Subject</Localize>
                </span>
                <strong
                  className="communication-task-form__reply-draft-address-value"
                  title={replyDraftSubject || task.subject}
                >
                  {replyDraftSubject || task.subject}
                </strong>
              </div>
            </div>

            {hasReplyHandoffNote ? (
              <div className="communication-task-form__preview-line communication-task-form__preview-line--multiline">
                {resolvedReplyHandoffNote}
              </div>
            ) : (
              <div className="communication-task-form__preview-empty">
                <Localize>COMMUNICATION.HandoffNote_EmptyPreview</Localize>
              </div>
            )}

            <div className="communication-task-form__next-action-meta">
              <span className={`communication-task-form__question-meta-chip${hasReplyHandoffNote ? "" : " communication-task-form__question-meta-chip--muted"}`}>
                <Localize>
                  {hasReplyHandoffNote
                    ? "COMMUNICATION.HandoffNote_StatusPrepared"
                    : "COMMUNICATION.HandoffNote_StatusEmpty"}
                </Localize>
              </span>
              {followUpStandNumber && needsCustomerFollowUp && (
                <span className="communication-task-form__question-meta-chip">
                  {localizeByKeyStatic("COMMUNICATION.QuestionsWorkflow_RelatedStand", {
                    stand: formatCanonicalStandNumberLabel(followUpStandNumber),
                  })}
                </span>
              )}
              {needsCustomerFollowUp && (
                <span className="communication-task-form__question-meta-chip communication-task-form__question-meta-chip--warning">
                  <Localize>COMMUNICATION.QuestionsWorkflow_ClarificationNeeded</Localize>
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {auditEntry && (
        <div className="communication-task-form__section">
          <h4><Localize>COMMUNICATION.Section_Audit</Localize></h4>
          <div className="communication-audit-box">
            <div>
              <strong><Localize>COMMUNICATION.Audit_Outcome</Localize>:</strong>{" "}
              <Localize>
                {auditEntry.outcome === "dismissed"
                  ? "COMMUNICATION.AuditOutcome_Dismissed"
                  : "COMMUNICATION.AuditOutcome_Approved"}
              </Localize>
            </div>
            <div>
              <strong><Localize>COMMUNICATION.Table_ProcessedBy</Localize>:</strong> {auditEntry.processed_by}
            </div>
            <div>
              <strong><Localize>COMMUNICATION.Table_ProcessedAt</Localize>:</strong>{" "}
              {formatCommunicationDateTime(auditEntry.processed_at)}
            </div>
            {auditEntry.outcome === "dismissed" && auditEntry.dismissed_reason && (
              <div>
                <strong><Localize>COMMUNICATION.Audit_Reason</Localize>:</strong>{" "}
                {localizeByKeyStatic(`COMMUNICATION.NotRelevantReason_${auditEntry.dismissed_reason}`)}
              </div>
            )}
            {auditEntry.excluded_context_groups && auditEntry.excluded_context_groups.length > 0 && (
              <div>
                <strong>Excluded context groups:</strong>{" "}
                {auditEntry.excluded_context_groups
                  .map((group) => [ group.event_name, group.company_name, group.stand_number ].filter(Boolean).join(" / "))
                  .join(", ")}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="form-btn-holder communication-task-form__actions">
        <div className="communication-task-form__actions-start">
          {shouldShowMarkAsNotRelevant && (
            <Button
              className="communication-task-form__dismiss-btn"
              onClick={() => setIsDismissModalOpen(true)}
            >
              <span className="ellipsis">
                <Localize>COMMUNICATION.Button_MarkNotRelevant</Localize>
              </span>
            </Button>
          )}

          <Button className="light-bg" onClick={() => action?.()}>
            <span className="ellipsis">
              <Localize>GLOBAL.Button_Text_Cancel</Localize>
            </span>
          </Button>

          {!isDismissedHistoryEntry && currentStep > 1 && (
            <Button
              className="light-bg"
              onClick={() => setCurrentStep((step) => Math.max(1, step - 1))}
            >
              <span className="ellipsis">
                <span className="communication-task-form__nav-arrow" aria-hidden={true}>{"\u2190"}</span>
                {localizeByKeyStatic(backButtonLabelKey)}
              </span>
            </Button>
          )}
        </div>

        <div className="communication-task-form__actions-end">
          {!isDismissedHistoryEntry && !isLastStep && (
            <Button
              type="primary"
              onClick={handleNextStep}
            >
              <span className="ellipsis">
                {localizeByKeyStatic(nextButtonLabelKey)}
                <span className="communication-task-form__nav-arrow" aria-hidden={true}>{"\u2192"}</span>
              </span>
            </Button>
          )}

          {!isDismissedHistoryEntry && isLastStep && canApprove && !isAuditView && (
            <Button
              type="primary"
              className="btn-right"
              icon={<Icons.Check />}
              onClick={handleApproveClick}
            >
              <span className="ellipsis">
                <Localize>COMMUNICATION.Button_ApproveAndComplete</Localize>
              </span>
            </Button>
          )}
        </div>
      </div>

      <Modal
        open={isDismissModalOpen}
        onCancel={() => setIsDismissModalOpen(false)}
        onOk={handleDismissNotRelevant}
        okText={localizeByKeyStatic("COMMUNICATION.NotRelevant_Confirm")}
        okButtonProps={{ className: "communication-task-form__dismiss-confirm-btn" }}
        cancelText={localizeByKeyStatic("GLOBAL.Button_Text_Cancel")}
        cancelButtonProps={{ className: "communication-task-form__dismiss-cancel-btn" }}
        title={localizeByKeyStatic("COMMUNICATION.NotRelevant_Title")}
        wrapClassName="communication-task-form__dismiss-modal-wrap"
      >
        <div className="communication-task-form__dismiss-modal">
          <p className="communication-task-form__dismiss-hint">
            {localizeByKeyStatic(
              hasDismissReviewSignals
                ? "COMMUNICATION.NotRelevant_HintWithExtractedData"
                : "COMMUNICATION.NotRelevant_Hint"
            )}
          </p>

          <Form.Item
            label={localizeByKeyStatic("COMMUNICATION.NotRelevant_ReasonLabel")}
            className="communication-task-form__dismiss-field"
          >
            <Select
              value={dismissReason}
              options={dismissReasonOptions}
              suffixIcon={<Icons.Arrow />}
              onChange={(value) => setDismissReason(value as TCommunicationDismissReason)}
            />
          </Form.Item>
        </div>
      </Modal>
    </Form>
  );
};

export default CommunicationTaskForm;

